<template>
  <div class="fc-cxp">
    <!-- ───────── HERO ───────── -->
    <header class="fc-hero">
      <div class="fc-hero__left">
        <span class="fc-hero__eyebrow">
          <v-icon size="16" color="primary">mdi-receipt-text-outline</v-icon>
          Módulo · Pagos a suplidores
        </span>
        <h1 class="fc-hero__title">Cuentas por pagar</h1>
        <p class="fc-hero__lede">
          Gestiona facturas de compra, registra pagos y analiza la antigüedad de
          deuda para <strong>{{ currentBusinessUnitName }}</strong>.
        </p>
      </div>

      <div class="fc-hero__right">
        <div class="fc-hero__date">
          <span class="fc-hero__date-day">{{ today.day }}</span>
          <span class="fc-hero__date-rest">
            <span class="fc-hero__date-month">{{ today.month }}</span>
            <span class="fc-hero__date-year">{{ today.year }}</span>
          </span>
        </div>
        <span class="fc-chip-live">
          <span class="fc-chip-live__dot"></span>
          Datos en vivo
        </span>
      </div>
    </header>

    <!-- ───────── FILTROS ───────── -->
    <section class="fc-section">
      <div class="fc-section__head">
        <v-icon size="20" color="primary">mdi-filter-variant</v-icon>
        <h2 class="fc-section__title">Filtros</h2>
      </div>
      <div class="fc-card fc-card--filters">
        <v-row align="center" class="ma-0">
      <!-- UNIDAD DE NEGOCIOS -->
      <v-col cols="12" sm="auto" md="auto" lg="auto" class="py-2">
        <v-autocomplete v-model="business_selected" :items="itemsBussines" label="Unidad de negocio" color="primary"
          item-color="primary" prepend-inner-icon="mdi-office-building-outline" outlined dense
          @change="updateLocationsForBusiness(true)" hide-details>
          <template v-slot:prepend-inner>
            <v-icon color="primary">mdi-office-building-outline</v-icon>
          </template>
        </v-autocomplete>
      </v-col>
      <v-col cols="12" sm="auto" md="auto" lg="auto" class="py-2">
        <v-autocomplete v-model="location_selected" :items="itemsLocations2" return-object color="primary"
          item-color="primary" label="Sucursal" outlined dense @change="update_filters()" hide-details>
          <template v-slot:prepend-inner>
            <v-icon color="primary">mdi-map-marker-outline</v-icon>
          </template>
        </v-autocomplete>
      </v-col>
      <v-col cols="12" class="pl-2" md="auto" v-if="useRangeFilter">
        <v-menu v-model="menu_date_start" :close-on-content-click="false" transition="slide-y-transition" offset-y
          min-width="auto" max-width="290px">
          <template v-slot:activator="{ on, attrs }">
            <v-text-field v-model="dateStartFormatDMY" label="Fecha inicio" readonly v-bind="attrs" v-on="on" outlined
              dense hide-details color="primary">
              <template v-slot:prepend-inner>
                <v-icon color="primary">mdi-calendar-start</v-icon>
              </template>
            </v-text-field>
          </template>
          <v-date-picker v-model="dateStart" @input="menu_date_start = false, update_filters()" color="primary"
            header-color="primary" show-current locale="es"></v-date-picker>
        </v-menu>
      </v-col>
      <v-col cols="12" class="pl-2" md="auto">
        <v-menu v-model="menu_date_end" :close-on-content-click="false" transition="slide-y-transition" offset-y
          min-width="auto" max-width="290px">
          <template v-slot:activator="{ on, attrs }">
            <v-text-field v-model="date2FormatDMY" label="Fecha fin" readonly v-bind="attrs" v-on="on" outlined dense
              hide-details color="primary">
              <template v-slot:prepend-inner>
                <v-icon color="primary">mdi-calendar-end</v-icon>
              </template>
            </v-text-field>
          </template>
          <v-date-picker v-model="date2" @input="menu_date_end = false, update_filters()" color="primary"
            header-color="primary" show-current locale="es"></v-date-picker>
        </v-menu>
      </v-col>
      <v-col cols="12" class="pl-2" md="auto">
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon :color="useRangeFilter ? 'primary' : 'grey'" @click="toggleRangeFilter" v-bind="attrs"
              v-on="on">
              <v-icon>{{ useRangeFilter ? 'mdi-calendar-range' : 'mdi-calendar' }}</v-icon>
            </v-btn>
          </template>
          <span>{{ useRangeFilter ? 'Usar solo fecha fin' : 'Usar rango de fechas' }}</span>
        </v-tooltip>
      </v-col>
      <v-col cols="12" class="pl-2" md="auto">
        <v-select v-model="aging_filter" :items="aging_options" label="Antigüedad" outlined dense hide-details clearable
          @change="update_filters()" color="primary">
          <template v-slot:prepend-inner>
            <v-icon color="primary">mdi-clock-alert-outline</v-icon>
          </template>
        </v-select>
      </v-col>
      <!-- Buscador global de suplidores: abre el estado de cuenta -->
      <v-col cols="12" class="pl-2" md="4" lg="3">
        <v-autocomplete v-model="supplierSearch" :items="supplierSearchItems" :loading="supplierSearchLoading"
          :search-input.sync="supplierSearchText" item-text="entity_name" item-value="entity_id" return-object
          label="Estado de cuenta: buscar suplidor por nombre o RNC" outlined dense hide-details clearable no-filter
          hide-no-data color="primary" @change="openStatementFromSearch">
          <template v-slot:prepend-inner>
            <v-icon color="primary">mdi-file-account-outline</v-icon>
          </template>
          <template v-slot:item="{ item }">
            <v-list-item-content>
              <v-list-item-title>{{ item.entity_name }}</v-list-item-title>
              <v-list-item-subtitle>{{ item.document_id || 'Sin documento' }} · {{ item.invoices_in_unit }} facturas en esta unidad</v-list-item-subtitle>
            </v-list-item-content>
          </template>
        </v-autocomplete>
      </v-col>
        </v-row>
      </div>
    </section>

    <!-- ───────── TABS ───────── -->
    <div class="fc-card fc-card--board">
      <div :style="{ position: 'relative' }">
        <v-overlay :value="loaded" absolute z-index="10" opacity="0.3">
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
        </v-overlay>
        <v-tabs v-model="types_form" background-color="transparent" color="primary" slider-color="primary"
          slider-size="3" grow class="fc-tabs" :style="{ pointerEvents: loaded ? 'none' : 'auto' }">
          <v-tab class="fc-tab">
            <v-icon left size="18">mdi-cash-multiple</v-icon>
            Pagos múltiples
          </v-tab>
          <v-tab class="fc-tab">
            <v-icon left size="18">mdi-account-group</v-icon>
            Pagos por suplidor
          </v-tab>
          <v-tab class="fc-tab">
            <v-icon left size="18">mdi-cash-register</v-icon>
            Pagos realizados
          </v-tab>
          <v-tab class="fc-tab">
            <v-icon left size="18">mdi-file-chart</v-icon>
            Reportes
          </v-tab>
        </v-tabs>
      </div>
    </div>

    <!-- ───────── CONTENIDO ───────── -->
    <v-tabs-items v-model="types_form" class="fc-panes">
          <v-tab-item v-for="page in 4" :key="page - 1" class="mt-6">
            <!-- ----FILTROS---- -->
            <!-- <v-row justify="center" class="mt-2" v-if="page < 4">
              <v-col cols="12">
                <search-header
                  @dataFilters="changeBusiness($event)"
                  :selected_week="2"
                  :type="false"
                  :show_department="false"

                />
              </v-col>
            </v-row> -->
            <!-- ═══════ STATS CARDS (Page 1 - Pagos múltiples) ═══════ -->
            <div v-if="hasData && page < 3" class="fc-kpis mb-4">
              <!-- Stats page 1 (Pagos múltiples) -->
              <template v-if="page == 1">
                <article v-for="(stat, index) in getStats(page)" :key="'s' + index" class="fc-kpi"
                  :class="`fc-kpi--${stat.color}`">
                  <div class="fc-kpi__top">
                    <span class="fc-kpi__icon">
                      <v-icon size="22" :color="stat.color">{{ getStatIcon(stat.title) }}</v-icon>
                    </span>
                  </div>
                  <span class="fc-kpi__label">{{ stat.title }}</span>
                  <div class="fc-kpi__values">
                    <div class="fc-kpi__money">
                      <span class="fc-kpi__number" v-if="stat.format === 'currency'">{{ currencyFormat(stat.value) }}</span>
                      <span class="fc-kpi__number" v-else-if="stat.format === 'integer'">{{ stat.value.toLocaleString('en-US') }}</span>
                      <span class="fc-kpi__number" v-else>{{ stat.value }}</span>
                    </div>
                  </div>
                </article>
              </template>
              <!-- Stats page 2 (Pagos por suplidor) -->
              <template v-else>
                <article v-for="(stat, index) in consolidatedStats" :key="'c' + index" class="fc-kpi"
                  :class="`fc-kpi--${stat.color}`">
                  <div class="fc-kpi__top">
                    <span class="fc-kpi__icon">
                      <v-icon size="22" :color="stat.color">{{ getStatIcon(stat.title) }}</v-icon>
                    </span>
                  </div>
                  <span class="fc-kpi__label">{{ stat.title }}</span>
                  <div class="fc-kpi__values">
                    <template v-if="stat.isSingleValue">
                      <div class="fc-kpi__money">
                        <span class="fc-kpi__number">{{ formatValue(stat) }}</span>
                      </div>
                    </template>
                    <template v-else>
                      <div v-for="(value, idx) in stat.values" :key="idx" class="fc-kpi__money">
                        <span class="fc-kpi__number">{{ currencyFormat(value.amount) }}</span>
                        <span class="fc-kpi__currency">{{ value.currencyCode }}</span>
                      </div>
                    </template>
                  </div>
                </article>
              </template>
            </div>

            <!-- ═══════ SELECTION INFO BAR (Page 1) ═══════ -->
            <v-card v-if="page === 1" flat class="selection-bar mt-2"
              :class="{ 'selection-bar--active': selected.length > 0 }">
              <v-card-text class="py-2 px-4">
                <v-row align="center" no-gutters>
                  <!-- Facturas Seleccionadas -->
                  <v-col cols="12" sm="6" md="3" class="d-flex align-center selection-item">
                    <v-icon small class="mr-2"
                      :color="selected.length > 0 ? 'primary' : 'grey'">mdi-checkbox-marked-outline</v-icon>
                    <span class="selection-label">Facturas:</span>
                    <v-chip x-small :color="selected.length > 0 ? 'primary' : 'grey lighten-1'"
                      :dark="selected.length > 0" class="ml-2 font-weight-bold">
                      {{ selected.length }}
                    </v-chip>
                  </v-col>
                  <!-- Monto Seleccionado -->
                  <v-col cols="12" sm="6" md="3" class="d-flex align-center selection-item">
                    <v-icon small class="mr-2"
                      :color="pendingAmountTotal > 0 ? 'warning darken-2' : 'grey'">mdi-currency-usd</v-icon>
                    <span class="selection-label">Seleccionado:</span>
                    <span class="selection-value ml-2 font-weight-bold"
                      :class="pendingAmountTotal > 0 ? 'warning--text text--darken-2' : 'grey--text'">
                      {{ currencyFormat(pendingAmountTotal) }}
                    </span>
                  </v-col>
                  <!-- Monto disponible -->
                  <v-col cols="12" sm="6" md="3" class="d-flex align-center selection-item">
                    <v-icon small class="mr-2" color="info">mdi-wallet-outline</v-icon>
                    <span class="selection-label">Disponible:</span>
                    <v-text-field v-model="amount_to_pay" dense type="number" :min="1" hide-details
                      class="ml-2 selection-input" style="max-width: 130px;" prefix="$"></v-text-field>
                  </v-col>
                  <!-- Monto restante -->
                  <v-col cols="12" sm="6" md="3" class="d-flex align-center selection-item">
                    <v-icon small class="mr-2" :color="available_to_pay.color">mdi-cash-refund</v-icon>
                    <span class="selection-label">Restante:</span>
                    <span class="selection-value ml-2 font-weight-bold" :class="`${available_to_pay.color}--text`">
                      {{ currencyFormat(available_to_pay.amount) }}
                    </span>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
            <div class="fc-card fc-card--table mt-2" v-if="page < 4">
              <div class="fc-table__head">
                <span class="fc-table__title">
                  <v-icon color="primary" size="20" class="mr-2">{{ getIconForStep(page) }}</v-icon>
                  {{ getTitleForStep(page) }}
                </span>
                <v-divider class="mx-4" inset vertical></v-divider>
                <v-btn-toggle v-if="page === 1" v-model="seleted_currencies" class="mr-2" mandatory color="primary">
                  <v-btn v-for="(item, i) of currencies" :key="i" :value="item.id" small>
                    {{ item.code }}
                  </v-btn>
                </v-btn-toggle>
                <v-spacer></v-spacer>
                <!-- Buscador para Pagos por suplidor (page 2) -->
                <v-col md="3" v-if="page === 2">
                  <v-text-field v-model="search" append-icon="mdi-magnify" label="Buscar suplidor" outlined dense
                    hide-details clearable placeholder="Nombre del suplidor..."></v-text-field>
                </v-col>
                <!-- Buscador para Pagos realizados (page 3) -->
                <v-col md="3" v-if="page === 3">
                  <v-text-field v-model="search_payment" append-icon="mdi-magnify" label="Buscar pago (Enter)" outlined
                    dense hide-details clearable placeholder="Suplidor, usuario, # pago..."
                    @keyup.enter="searchPayments()" @click:clear="search_payment = '', searchPayments()"></v-text-field>
                </v-col>
                <v-btn v-if="page === 3" color="success" outlined class="mr-2 text-none"
                  @click="export_payment_details()" :loading="loading_export" :disabled="!lastPaymentsFilter">
                  <v-icon left small>mdi-file-excel-outline</v-icon>
                  Excel
                </v-btn>
                <v-menu v-if="page === 3" v-model="dialog_filters" :close-on-content-click="false" offset-y left
                  nudge-bottom="4" min-width="320">
                  <template v-slot:activator="{ on, attrs }">
                    <v-badge :value="active_filters_count > 0" :content="active_filters_count" color="primary" overlap>
                      <v-btn icon v-bind="attrs" v-on="on" :color="active_filters_count > 0 ? 'primary' : 'grey darken-1'"
                        @click="openFiltersMenu()">
                        <v-icon>mdi-filter-outline</v-icon>
                      </v-btn>
                    </v-badge>
                  </template>
                  <v-card width="320">
                    <v-card-title class="subtitle-2 pb-2">Filtros adicionales</v-card-title>
                    <v-card-text class="pb-0">
                      <v-autocomplete v-model="filter_supplier" :items="filter_suppliers_list" label="Suplidor"
                        outlined dense hide-details clearable class="mb-3" prepend-inner-icon="mdi-account-outline"
                        :loading="filter_suppliers_loading" no-data-text="Sin suplidores en el rango seleccionado">
                      </v-autocomplete>
                      <v-select v-model="filter_status_id" :items="payment_states" item-text="name" item-value="id"
                        label="Estatus" outlined dense hide-details clearable class="mb-3"
                        prepend-inner-icon="mdi-flag-outline" no-data-text="Cargando...">
                      </v-select>
                      <v-autocomplete v-model="filter_bank_account_ids" :items="filter_bank_accounts_list"
                        item-text="text" item-value="account_id" label="Cuentas de banco" outlined dense hide-details
                        clearable multiple small-chips deletable-chips class="mb-3"
                        prepend-inner-icon="mdi-bank-outline"
                        no-data-text="Sin cuentas para la unidad de negocio seleccionada">
                        <template v-slot:selection="{ item, index }">
                          <v-chip v-if="index < 2" small close
                            @click:close="removeBankAccountFilter(item.account_id)">
                            <span class="text-truncate" style="max-width: 150px">{{ item.text }}</span>
                          </v-chip>
                          <span v-else-if="index === 2" class="grey--text caption">
                            (+{{ filter_bank_account_ids.length - 2 }} más)
                          </span>
                        </template>
                      </v-autocomplete>
                      <v-text-field v-model="filter_invoice_number" label="Número de factura" outlined dense hide-details
                        clearable placeholder="Ej: F-001234..." prepend-inner-icon="mdi-file-document-outline">
                      </v-text-field>
                    </v-card-text>
                    <v-card-actions class="pt-2">
                      <v-btn text small color="grey" @click="filter_supplier = null; filter_status_id = null; filter_invoice_number = ''; filter_bank_account_ids = []; applyFilters()">
                        Limpiar
                      </v-btn>
                      <v-spacer></v-spacer>
                      <v-btn color="primary" small @click="applyFilters()">
                        <v-icon left small>mdi-magnify</v-icon>
                        Aplicar
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </v-menu>
                <!-- Controles de Pagos múltiples (page 1) -->
                <v-col md="2">
                  <v-text-field v-if="page === 1" v-model="search_invoice" append-icon="mdi-magnify"
                    label="Buscar factura (Enter)" outlined dense hide-details @keyup.enter="update_filters()"
                    class="mr-2" placeholder="Número de factura..."></v-text-field>
                </v-col>
                <v-col md="2">
                  <v-autocomplete v-if="page === 1" label="Suplidores" v-model="supplier_filter" item-text="entity_name"
                    item-value="entity_id" outlined dense hide-details :items="supplier_filtered"
                    @change="update_filters()"></v-autocomplete>
                </v-col>
                <v-btn v-if="page === 1" color="primary" outlined class="mr-2"
                  @click="preview_type = '1', dialog_print_preview = true" :disabled="loaded">
                  <v-icon left>mdi-printer</v-icon>Previo
                </v-btn>
                <v-btn v-if="page === 1 && hasFeature('CXP-022')" @click="handlePay" color="success" outlined :disabled="selected.length === 0">
                  <v-icon left>mdi-cash-multiple</v-icon>
                  Pagar
                  <v-badge v-if="selected.length > 0" :content="selected.length" color="red" overlap class="ml-2"
                    offset-y="-10" offset-x="-5"></v-badge>
                </v-btn>
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon color="light-blue" class="ml-2" @click="selected = []" v-bind="attrs" v-on="on"
                      v-if="page === 1" :disabled="selected.length === 0">
                      <v-icon>
                        cleaning_services
                      </v-icon>
                    </v-btn>
                  </template>
                  <span>Limpiar seleccion</span>
                </v-tooltip>
              </div>

              <v-data-table :headers="getHeadersForStep(page)" :items="getItemsForStep(page)" :search="search"
                :items-per-page="page === 1 ? itemsPerPage : (page === 3 ? transactionsLimit : itemsPerPageSupplier)"
                :server-items-length="page === 1 ? totalItems : (page === 3 ? transactionsTotal : undefined)"
                :loading="page === 3 ? loading : loaded"
                :loading-text="page === 3 ? 'Cargando pagos...' : 'Cargando facturas...'" :show-select="page === 1"
                v-model="selected" item-key="invoice_id" class="fc-data-table elevation-0" hide-default-footer
                :page="page === 2 ? supplierPage : 1" @update:page="v => onTablePageUpdate(page, v)" @pagination="p => onTablePagination(page, p)">
                <template v-slot:[`item.invoice_payment_id`]="{ item }">
                  <v-hover v-slot="{ hover }">
                    <span @click="showPayments(item.invoice_payment_id, 2)"
                      class="primary--text font-weight-normal text-truncate font-weight-semibold" :style="{
                        color: hover ? 'red' : 'black',
                        cursor: hover ? 'pointer' : '',
                        'text-decoration': hover ? 'underline' : '',
                      }">
                      {{ filLeft(item.invoice_payment_id.toString(), 12, "0") }}
                    </span>
                  </v-hover>
                </template>
                <template v-slot:item.invoice_number="{ item }">
                  <v-row>
                    <v-hover v-slot="{ hover }">
                      <span @click="showInvoice(item.invoice_id, true)" class="primary--text font-weight-medium"
                        style="cursor: pointer" :class="{
                          'red--text': hover,
                          'text-decoration-underline': hover,
                        }">
                        {{ item.invoice_number }}
                      </span>
                    </v-hover>
                  </v-row>
                  <v-row>
                    <small>{{ item.supplier_invoice_number }}</small>
                  </v-row>
                </template>

                <template v-slot:item.entity_name="{ item }">
                  <v-hover v-slot="{ hover }">
                    <span @click="viewEntity(item.entity_id, true)" class="primary--text font-weight-medium"
                      style="cursor: pointer" :class="{ 'red--text': hover, 'text-decoration-underline': hover }">
                      {{ item.entity_name }}
                    </span>
                  </v-hover>
                </template>
                <template v-slot:[`item.entity`]="{ item }">
                  <v-menu v-if="Object.keys(groupByEntityId(item.payment_details)).length > 1" offset-y open-on-hover>
                    <template v-slot:activator="{ on, attrs }">
                      <v-chip color="primary" outlined small v-bind="attrs" v-on="on">
                        <v-icon left small>mdi-account-group</v-icon>
                        Varios Suplidores ({{ Object.keys(groupByEntityId(item.payment_details)).length }})
                      </v-chip>
                    </template>
                    <v-list dense style="max-height: 300px; overflow-y: auto;">
                      <v-list-item v-for="(entity, id) in groupByEntityId(item.payment_details)" :key="id">
                        <v-list-item-title>{{ entity[0].entity_name }}</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                  <span v-else>
                    {{ Object.values(groupByEntityId(item.payment_details))[0][0].entity_name }}
                  </span>
                </template>
                <template v-slot:[`item.accounting`]="{ item }">
                  <v-chip small color="success" v-if="item.recorded">
                    <span>Contabilizado </span>
                  </v-chip>
                  <v-chip small color="warning" v-else>
                    <span class="text--primary">Sin contabilizar </span>
                  </v-chip>
                </template>
                <!-- aqui -->
                <template v-slot:item.total_pending_amount="{ item }">
                  <div class="error--text font-weight-medium">
                    <template v-if="item.details && item.details.length">
                      <div v-for="(amount, currency) in item.details.reduce(
                        (acc, detail) => {
                          if (!acc[detail.currency_code]) {
                            acc[detail.currency_code] = 0;
                          }
                          acc[detail.currency_code] +=
                            parseFloat(detail.pending_amount) || 0;
                          return acc;
                        },
                        {}
                      )" :key="currency">
                        {{ currencyFormat(amount) }} {{ currency }}
                      </div>
                    </template>
                    <template v-else>
                      {{ currencyFormat(0) }}
                    </template>
                  </div>
                </template>
                <template v-slot:item.total_debit_amount="{ item }">
                  <div class="warning--text font-weight-medium">
                    <template v-if="item.details && item.details.length">
                      <div v-for="(amount, currency) in item.details.reduce(
                        (acc, detail) => {
                          if (!acc[detail.currency_code]) {
                            acc[detail.currency_code] = 0;
                          }
                          acc[detail.currency_code] +=
                            parseFloat(detail.total_debits) || 0;
                          return acc;
                        },
                        {}
                      )" :key="currency">
                        {{ currencyFormat(amount) }} {{ currency }}
                      </div>
                    </template>
                    <template v-else>
                      {{ currencyFormat(0) }}
                    </template>
                  </div>
                </template>

                <template v-slot:item.total_credit_amount="{ item }">
                  <div class="warning--text font-weight-medium">
                    <template v-if="item.details && item.details.length">
                      <div v-for="(amount, currency) in item.details.reduce(
                        (acc, detail) => {
                          if (!acc[detail.currency_code]) {
                            acc[detail.currency_code] = 0;
                          }
                          acc[detail.currency_code] +=
                            parseFloat(detail.total_credits) || 0;
                          return acc;
                        },
                        {}
                      )" :key="currency">
                        {{ currencyFormat(amount) }} {{ currency }}
                      </div>
                    </template>
                    <template v-else>
                      {{ currencyFormat(0) }}
                    </template>
                  </div>
                </template>

                <template v-slot:item.payment_date="{ item }">
                  <span class="font-weight-medium">
                    {{ getDate(item.payment_date) }}
                  </span>
                </template>
                <template v-slot:item.total_amount="{ item }">
                  <span>
                    {{ currencyFormat(item.total_amount) }}
                    <small>{{ item.currency_code }}</small>
                  </span>
                </template>
                <template v-slot:item.total_to_pay="{ item }">
                  <span>
                    {{ currencyFormat(item.total_to_pay) }}
                    <small>{{ item.currency_code }}</small>
                  </span>
                </template>
                <template v-slot:item.pending_amount="{ item }">
                  <span class="error--text font-weight-medium">
                    {{ currencyFormat(item.pending_amount) }}
                    <small>{{ item.currency_code }}</small>
                  </span>
                </template>

                <template v-slot:item.total_paid="{ item }">
                  <span class="success--text font-weight-medium">
                    {{ currencyFormat(item.total_paid) }}
                    <small>{{ item.currency_code }}</small>
                  </span>
                </template>

                <template v-slot:item.emission_date="{ item }">
                  <span class="text-body-2">
                    {{ getDate(item.emission_date) }}
                  </span>
                </template>

                <template v-slot:item.expire_at="{ item }">
                  <v-chip small :color="getDateColor(item)" dark class="font-weight-bold" label>
                    {{ getDate(item.expire_at) }}
                  </v-chip>
                </template>

                <template v-slot:item.days_overdue="{ item }">
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-chip small :color="getDaysOverdueColor(item)" dark class="font-weight-bold" v-bind="attrs"
                        v-on="on">
                        <v-icon small left v-if="getDaysOverdue(item) > 0">mdi-alert-circle</v-icon>
                        <v-icon small left v-else-if="getDaysOverdue(item) < 0">mdi-clock-outline</v-icon>
                        <v-icon small left v-else>mdi-check-circle</v-icon>
                        {{ Math.abs(getDaysOverdue(item)) }}
                      </v-chip>
                    </template>
                    <span>{{ getDaysOverdueTooltip(item) }}</span>
                  </v-tooltip>
                </template>

                <template v-slot:item.status_id="{ item }">
                  <v-chip small :color="[1,2].includes(item.status_id) ? 'warning' : item.status_id === 3 ? 'success' : item.status_id === 4 ? 'error' : 'primary'" outlined>
                    {{ item.status_name }}
                  </v-chip>
                </template>

                <template v-slot:item.actions="{ item }">
                  <v-tooltip top v-if="hasFeature('CXP-022')">
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn icon small color="primary" v-bind="attrs" v-on="on" :disabled="loaded" @click="viewInvoices(item)">
                        <v-icon>mdi-cash-plus</v-icon>
                      </v-btn>
                    </template>
                    <span>Agregar pago</span>
                  </v-tooltip>
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn icon small color="primary" v-bind="attrs" v-on="on" @click="openStatement(item.entity_id)">
                        <v-icon>mdi-file-account-outline</v-icon>
                      </v-btn>
                    </template>
                    <span>Estado de cuenta</span>
                  </v-tooltip>
                </template>

                <template v-slot:no-data>
                  <v-row justify="center" align="center" class="py-10">
                    <v-icon size="64" color="grey lighten-1">mdi-database-off</v-icon>
                  </v-row>
                  <v-row justify="center" class="pb-6">
                    <span class="text-subtitle-1 grey--text">No hay datos disponibles</span>
                  </v-row>
                </template>
                <!-- ═══════ FOOTER PAGE 1 — Pagos múltiples ═══════ -->
                <template v-if="page === 1" slot="footer">
                  <div class="table-footer">
                    <div class="table-footer__left">
                      <v-icon small color="grey" class="mr-1">mdi-view-list</v-icon>
                      <span class="table-footer__label">Mostrar</span>
                      <v-btn-toggle v-model="itemsPerPage" mandatory dense class="table-footer__toggle mx-2"
                        color="primary" @change="onItemsPerPageChange">
                        <v-btn v-for="opt in itemsPerPageOptions" :key="opt" :value="opt" x-small outlined
                          class="table-footer__toggle-btn">
                          {{ opt }}
                        </v-btn>
                      </v-btn-toggle>
                    </div>
                    <div class="table-footer__center">
                      <v-chip small outlined color="grey darken-1" class="table-footer__info-chip">
                        <v-icon small left>mdi-file-document-outline</v-icon>
                        {{ (currentPage - 1) * itemsPerPage + 1 }}–{{ Math.min(currentPage * itemsPerPage, totalItems)
                        }}
                        <span class="mx-1 grey--text text--lighten-1">de</span>
                        <strong>{{ totalItems.toLocaleString() }}</strong>
                      </v-chip>
                    </div>
                    <div class="table-footer__right">
                      <v-pagination v-model="currentPage" :length="pageCount" :total-visible="7" color="primary" circle
                        :disabled="loaded" @input="onPageChange"></v-pagination>
                    </div>
                  </div>
                </template>
                <!-- ═══════ FOOTER PAGE 2 — Pagos por suplidor ═══════ -->
                <template v-if="page === 2" slot="footer">
                  <div class="table-footer">
                    <div class="table-footer__left">
                      <v-icon small color="grey" class="mr-1">mdi-view-list</v-icon>
                      <span class="table-footer__label">Mostrar</span>
                      <v-btn-toggle v-model="itemsPerPageSupplier" mandatory dense class="table-footer__toggle mx-2"
                        color="primary" @change="supplierPage = 1">
                        <v-btn v-for="opt in itemsPerPageOptions" :key="opt" :value="opt" x-small outlined
                          class="table-footer__toggle-btn">
                          {{ opt }}
                        </v-btn>
                      </v-btn-toggle>
                    </div>
                    <div class="table-footer__center">
                      <v-chip small outlined color="grey darken-1" class="table-footer__info-chip">
                        <v-icon small left>mdi-account-group-outline</v-icon>
                        {{ supplierPagination.itemsLength ? supplierPagination.pageStart + 1 : 0 }}–{{ supplierPagination.pageStop }}
                        <span class="mx-1 grey--text text--lighten-1">de</span>
                        <strong>{{ supplierPagination.itemsLength.toLocaleString() }}</strong>
                        <span class="ml-1">suplidores</span>
                      </v-chip>
                    </div>
                    <div class="table-footer__right">
                      <v-pagination v-model="supplierPage" :length="supplierPagination.pageCount" :total-visible="7"
                        color="primary" circle :disabled="loaded"></v-pagination>
                    </div>
                  </div>
                </template>
                <!-- ═══════ FOOTER PAGE 3 — Pagos realizados ═══════ -->
                <template v-if="page === 3" slot="footer">
                  <div class="table-footer">
                    <div class="table-footer__left">
                      <v-icon small color="grey" class="mr-1">mdi-view-list</v-icon>
                      <span class="table-footer__label">Mostrar</span>
                      <v-btn-toggle v-model="transactionsLimit" mandatory dense class="table-footer__toggle mx-2"
                        color="primary" @change="onTransactionsLimitChange">
                        <v-btn v-for="opt in itemsPerPageOptions" :key="opt" :value="opt" x-small outlined
                          class="table-footer__toggle-btn">
                          {{ opt }}
                        </v-btn>
                      </v-btn-toggle>
                    </div>
                    <div class="table-footer__center">
                      <v-chip small outlined color="grey darken-1" class="table-footer__info-chip">
                        <v-icon small left>mdi-cash-register</v-icon>
                        {{ (transactionsPage - 1) * transactionsLimit + 1 }}–{{ Math.min(transactionsPage *
                          transactionsLimit, transactionsTotal) }}
                        <span class="mx-1 grey--text text--lighten-1">de</span>
                        <strong>{{ transactionsTotal.toLocaleString() }}</strong>
                      </v-chip>
                    </div>
                    <div class="table-footer__right">
                      <v-pagination v-model="transactionsPage" :length="transactionsTotalPages" :total-visible="7"
                        color="primary" circle :disabled="loading" @input="onTransactionsPageChange">
                      </v-pagination>
                    </div>
                  </div>
                </template>
              </v-data-table>
            </div>

            <!-- Tab de Reportes -->
            <div v-if="page === 4" class="fc-card fc-card--board">
              <v-tabs v-model="reportSubTab" color="primary" slider-color="primary" slider-size="3"
                background-color="transparent" class="fc-tabs">
                <v-tab class="fc-tab"><v-icon left size="18">mdi-chart-bar</v-icon>Dashboard</v-tab>
                <v-tab class="fc-tab"><v-icon left size="18">mdi-microsoft-excel</v-icon>Excel / Reporte</v-tab>
              </v-tabs>
              <v-divider></v-divider>
              <div class="fc-board__pane">
                <v-tabs-items v-model="reportSubTab" style="background:transparent;">
                <v-tab-item>
                  <AgingReportDashboard :data="dashboardData" :loading="dashboardLoading"
                    :businessUnitName="currentBusinessUnitName" :locationName="currentLocationName"
                    :currencyName="currentCurrencyCode" :reportDate="date2" :currencies="currencies"
                    :allCurrencies="allSystemCurrencies" :filters="dashboardFilters"
                    :locationId="lastData ? lastData.location : 0" :currencyId="seleted_currencies"
                    :locationAccess="$store.state.auth.locations.map(l => l.id)"
                    :businessId="lastData ? lastData.business : 0" @currency-change="onDashboardCurrencyChange"
                    @convert-currency-change="onDashboardConvertChange" />
                </v-tab-item>
                <v-tab-item>
                  <AgingReportsvue :aging_type="1" />
                </v-tab-item>
                </v-tabs-items>
              </div>
            </div>
          </v-tab-item>
        </v-tabs-items>

    <v-dialog v-model="dialog" fullscreen transition="dialog-bottom-transition">
      <v-card>
        <v-card-text>
          <v-container fluid>
            <v-row justify="center">
              <v-col cols="12">
                <!-- <viewInvoices
                  v-if="!loaded"
                  @close="reset()"
                  :type="true"
                  :title="'Cuentas por pagar'"
                  :data_invoices="selectedClients"
                /> -->

                <viewInvoices class="m" v-if="!loaded" @close="reset()" :type="true" :title="'Cuentas por pagar'"
                  :data_invoices="selectedClients" @refreshInvoices="refreshInvoices()" :filters="filters" />
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- persistent: un clic fuera mientras el diálogo se anima no debe cerrarlo; se cierra con la X -->
    <v-dialog v-model="statementDialog" fullscreen transition="dialog-bottom-transition" scrollable persistent>
      <SupplierStatement v-if="statementDialog && statementEntityId" :entity-id="statementEntityId"
        :business-unit-id="Number(business_selected)" :location-id="statementLocationId"
        :location-access="$store.state.auth.locations.map(l => l.id)" :date-start="useRangeFilter ? dateStart : null"
        :date-end="date2" @close="statementDialog = false" />
    </v-dialog>

    <v-dialog v-model="dialogCancelPayment" max-width="400">
      <v-card>
        <v-card-title class="headline primary white--text">Anular pago</v-card-title>
        <v-card-text class="pt-4">
          <p class="text-h6 text-center">¿Seguro que deseas eliminar este pago?</p>
        </v-card-text>
        <v-card-actions class="justify-center pb-4">
          <v-btn text @click="dialogCancelPayment = false">Cancelar</v-btn>
          <v-btn color="error" outlined @click="cancelPayment">Anular pago</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showCheckOutCxC" fullscreen hide-overlay transition="dialog-bottom-transition">
      <v-card>
        <v-toolbar dark color="primary" flat>
          <v-btn icon dark @click="showCheckOutCxC = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title>Realizar pago</v-toolbar-title>
          <v-spacer></v-spacer>
        </v-toolbar>
        <v-card-text>
          <v-container fluid>
            <v-row justify="start">
              <v-col cols="12" md="3">
                <v-btn class="mx-2" color="error" outlined dense small @click="showCheckOutCxC = false">
                  <v-icon>arrow_back</v-icon> Volver
                </v-btn>
              </v-col>
            </v-row>

            <CheckOutCxC @close="refreshData" :data="data_invoices_pay" :type="1"
              :available_to_pay="{ amount: available_to_pay.amount, color: available_to_pay.color, amount_to_pay: amount_to_pay, pendingAmountTotal: pendingAmountTotal, selected_quantity: selected.length }"
              @updatePriceCurrency="updatePriceCurrency"
              @remove-invoice="removeInvoiceFromSelected" />
          </v-container>
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-dialog width="30%" v-model="dialog_print_preview">
      <v-card>
        <v-card-title>
          Imprimir previo
        </v-card-title>
        <v-card-text>
          <v-row align="center" justify="center">
            <v-col md="10">
              <v-autocomplete v-model="bank_to_preview" :items="bank_account_with_currency" item-text="account_name"
                item-value="account_id" label="Cuenta de banco" prepend-inner-icon="mdi-bank" outlined dense
                return-object required hide-details />
            </v-col>
          </v-row>
          <v-row justify="center" align="center">
            <v-col md="12" class="pb-0" justify="center" align="center">
              <span>Imprimir: </span>
            </v-col>
          </v-row>
          <v-row justify="center">
            <v-col cols="12" class="d-flex justify-center pt-0">
              <v-radio-group v-model="preview_type" class="d-flex flex-row justify-center align-center" column>
                <v-radio :label="`Todo (${totalItems} facturas)`" color="primary" value="1"></v-radio>

                <v-radio :label="`Seleccion (${selected.length} facturas)`" :disabled="selected.length == 0"
                  color="primary" value="2"></v-radio>
              </v-radio-group>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" outlined @click="generate_preview()" :loading="generatingPreview"
            :disabled="bank_to_preview == null || generatingPreview">Generar</v-btn>
          <v-btn text @click="dialog_print_preview = false" color="error">Cancelar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import AgingReportDashboard from "@/components/Transactions/AgingReportDashboard.vue";
import AgingReportsvue from "@/components/Accounting/AgingReportsvue.vue";
import viewInvoices from "@/components/Transactions/ViewInvoicesCxC.vue";
import CheckOutCxC from "@/components/Transactions/CheckOutCxC.vue";
import SupplierStatement from "@/components/Transactions/SupplierStatement.vue";
import moment from "moment";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import ExcelJS from "exceljs";
moment.locale("es");
export default {
  components: { viewInvoices, CheckOutCxC, AgingReportDashboard, AgingReportsvue, SupplierStatement },
  data: () => ({
    all_business_units: [],
    loading: false,
    loading_export: false,
    lastPaymentsFilter: '',
    dialogCancelPayment: false,
    showCheckOutCxC: false,
    types_form: 0,
    reportSubTab: 0,
    supplier_filter: 0,
    all_suppliers: [],
    last_filter: null,
    amount_to_pay: 0,
    loaded: true,
    filters: null,
    dialog: false,
    preview_type: '1',
    dialog_print_preview: false,
    bank_to_preview: null,
    search: "",
    search_invoice: "",
    search_payment: "",
    dialog_filters: false,
    filter_supplier: null,
    filter_status_id: null,
    filter_invoice_number: '',
    filter_bank_account_ids: [],
    filter_suppliers_list: [],
    filter_suppliers_loading: false,
    payment_states: [],
    clients: [],
    data_invoices: [],
    data_invoices_pay: [],
    selected: [],
    hasData: false,
    currentPage: 1,
    itemsPerPage: 15,
    itemsPerPageOptions: [15, 25, 50, 100],
    itemsPerPageSupplier: 15,
    // Estado de cuenta de suplidor + buscador global
    statementDialog: false,
    statementEntityId: null,
    supplierSearch: null,
    supplierSearchText: "",
    supplierSearchItems: [],
    supplierSearchLoading: false,
    supplierSearchTimer: null,
    generatingPreview: false,
    savedInvoiceDates: null,
    // Paginación client-side del tab "Pagos por suplidor" (la tabla es compartida por los 3 tabs)
    supplierPage: 1,
    supplierPagination: { pageStart: 0, pageStop: 0, itemsLength: 0, pageCount: 0 },
    totalItems: 0,
    pageCount: 0,
    globalTotals: {
      total_invoices: 0,
      total_pending: 0,
      total_credits: 0,
      total_paid: 0,
      total_debits: 0
    },
    headers: [
      { text: "Suplidor", value: "entity_name", align: "start" },
      // { text: "Cant facturas", value: "invoice_quantity", align: "end" },
      { text: "Notas de debito", value: "total_debit_amount", align: "end" },
      { text: "Notas de crédito", value: "total_credit_amount", align: "end" },
      { text: "Total Cobrado", value: "total_paid_amount", align: "end" },
      { text: "Balance pendiente", value: "total_pending_amount", align: "end" },
      { text: "Acciones", value: "actions", sortable: false, align: "center" },
    ],
    headersInvoices: [
      { text: "Factura", value: "invoice_number", align: "start" },
      { text: "Suplidor", value: "entity_name", align: "start" },
      { text: "Fecha efectiva", value: "emission_date", align: "end" },
      { text: "Vencimiento", value: "expire_at", align: "end" },
      { text: "Días", value: "days_overdue", align: "center", width: "100px" },
      { text: "Total factura", value: "total_amount", align: "end" },
      { text: "Total a pagar", value: "total_to_pay", align: "end" },
      { text: "Total Pagado", value: "total_paid", align: "end" },
      { text: "Balance pendiente", value: "pending_amount", align: "end" },
    ],
    transactions: [],
    transactionsPage: 1,
    transactionsLimit: 15,
    transactionsTotal: 0,
    transactionsTotalPages: 0,
    headersTransactions: [
      {
        text: "Grupo pago",
        align: "start",
        sortable: false,
        value: "invoice_payment_id",
      },
      { text: "Suplidor", value: "entity", width: "20%" },
      { text: "Usuario", value: "payment_by_name" },
      { text: "Monto total", align: "end", value: "total_amount" },
      { text: "Contabilidad", value: "accounting" },
      { text: "Fecha", value: "payment_date" },
      { text: "Status", value: "status_id" },
    ],
    currencies: [],
    selectPayments: null,
    lastData: null,
    selectedClients: [],
    editedIndex: -1,
    editedItem: [],
    seleted_currencies: 1,
    itemsBussines: [],
    itemsLocations2: [],
    itemsLocations: [],
    menu_date_end: false,
    menu_date_start: false,
    date2: moment().format("YYYY-MM-DD"),
    dateStart: moment().subtract(90, 'days').format("YYYY-MM-DD"),
    useRangeFilter: false,
    aging_filter: null,
    aging_options: [
      { text: 'Todas', value: null },
      { text: '0-30 días', value: '0-30' },
      { text: '31-60 días', value: '31-60' },
      { text: '61-90 días', value: '61-90' },
      { text: 'Más de 90 días', value: '90+' },
      { text: 'Vencidas', value: 'overdue' },
      { text: 'Por vencer', value: 'upcoming' }
    ],
    allSystemCurrencies: [],
    dashboardLoading: false,
    _dashboardCalculating: false,
    dashboardCurrencyChanging: false,
    dashboardConvertTo: false,
    dashboardCurrencyTo: null,
    dashboardFilters: '',
    dashboardData: {
      kpis: {
        totalPending: 0,
        overdueCount: 0,
        avgDPO: 0,
        activeSuppliers: 0,
        overdueAmount: 0
      },
      aging: {
        range0_30: 0,
        range31_60: 0,
        range61_90: 0,
        range91_120: 0,
        range120plus: 0
      },
      status: {
        overdue: 0,
        upcoming: 0,
        onTime: 0
      },
      topSuppliers: [],
      agingStacked: [],
      paretoData: [],
      locations: [],
      agingTable: [],
      trend: {
        months: [],
        invoices: [],
        payments: []
      },
      dpo: {
        value: 0
      },
      currencies: []
    }
  }),

  watch: {
    supplierSearchText(text) {
      clearTimeout(this.supplierSearchTimer);
      if (!text || text.trim().length < 2) { this.supplierSearchItems = []; return; }
      this.supplierSearchTimer = setTimeout(() => this.searchSuppliers(text.trim()), 300);
    },
    seleted_currencies() {
      this.selected = []; // Safety reset when currency changes
      if (this.dashboardCurrencyChanging) {
        this.dashboardCurrencyChanging = false;
        return;
      }
      if (this.types_form === 0) {
        this.getData(this.lastData);
      }
    },
    async types_form() {
      if (this.types_form === 0) {
        this.useRangeFilter = false;
        // Restaurar las fechas del listado de facturas si el tab de pagos las cambió
        if (this.savedInvoiceDates) {
          this.dateStart = this.savedInvoiceDates.dateStart;
          this.date2 = this.savedInvoiceDates.date2;
          this.savedInvoiceDates = null;
        }
        await this.update_filters();
      } else if (this.types_form === 2) {
        this.useRangeFilter = true;
        // El tab de pagos usa la semana actual por defecto; se guardan las fechas del listado
        // de facturas para restaurarlas al volver (antes quedaban pisadas).
        if (!this.savedInvoiceDates) {
          this.savedInvoiceDates = { dateStart: this.dateStart, date2: this.date2 };
        }
        const weekRange = this.getCurrentWeekRange();
        this.dateStart = weekRange.start;
        this.date2 = weekRange.end;

        // Call update_filters once after setting dates
        await this.update_filters();
      } else if (this.types_form === 1) {
        if (this.clients.length === 0) {
          this.getDataSupplier(this.lastData);
        }
      }

      if (this.types_form === 3) {
        await this.calculateDashboardData();
      }
    }
  },

  created() {
    this.itemsBussines = [...this.$store.state.auth.bussines_units];
    //this.itemsBussines.unshift({ text: "Todas", value: 0 });
    this.itemsLocations = [...(this.$store.state.auth.locations || [])];
    this.updateLocationsForBusiness();
    this.update_filters();
    this.get_accounts();
  },
  computed: {
    // El store compartido (filters.location) puede traer la sucursal como objeto
    // { value, text } o como id numérico, según la pantalla que la guardó.
    // 0 = Todas.
    locationId() {
      const loc = this.location_selected;
      if (loc && typeof loc === 'object') return Number(loc.value ?? loc.id) || 0;
      return Number(loc) || 0;
    },
    statementLocationId() {
      return this.locationId || null;
    },
    business_selected: {
      get() {
        return this.$store.state.app.filters.business || this.$store.state.auth.bussines_units[0]?.value;
      },
      set(value) {
        this.$store.commit("setBusiness", value);
      },
    },
    location_selected: {
      get() {
        return this.$store.state.app.filters.location;
      },
      set(value) {
        if (this.isValidLocationValue(value)) {
          this.$store.commit("setLocation", value);
        }
      },
    },
    currentBusinessUnitName() {
      const bu = this.itemsBussines.find(b => b.value === this.business_selected);
      return bu ? bu.text : 'Todas las Unidades';
    },
    currentCurrencyCode() {
      const cur = this.currencies.find(c => c.id === this.seleted_currencies);
      return cur ? cur.code : 'USD';
    },
    currentLocationName() {
      if (this.locationId) {
        const loc = this.itemsLocations2.find(l => l.value === this.locationId);
        return (loc && loc.text) || this.location_selected.text || '';
      }
      return '';
    },
    date2FormatDMY() {
      return moment(this.date2).format("DD/MM/YYYY");
    },
    dateStartFormatDMY() {
      return moment(this.dateStart).format("DD/MM/YYYY");
    },
    today() {
      const m = moment();
      return {
        day: m.format("DD"),
        month: m.format("MMMM").toUpperCase(),
        year: m.format("YYYY"),
      };
    },
    // Group amounts by currency
    statsByCurrency() {
      const currencyGroups = {};

      this.clients.forEach((client) => {
        client.details.forEach((currencyTotal) => {
          const currencyId = currencyTotal.currency_id;

          if (!currencyGroups[currencyId]) {
            currencyGroups[currencyId] = {
              invoice_quantity: 0,
              pending_amount: 0,
              total_credits: 0,
              total_debits: 0,
              total_paid: 0,
              currency_code: currencyTotal.currency_code,
            };
          }

          // Usando las propiedades de currencyTotal en lugar de client
          currencyGroups[currencyId].invoice_quantity +=
            parseInt(currencyTotal.invoice_count) || 0;
          currencyGroups[currencyId].pending_amount +=
            parseFloat(currencyTotal.pending_amount) || 0;
          currencyGroups[currencyId].total_credits +=
            parseFloat(currencyTotal.total_credits) || 0;
          currencyGroups[currencyId].total_paid +=
            parseFloat(currencyTotal.total_paid) || 0;
          currencyGroups[currencyId].total_debits +=
            parseFloat(currencyTotal.total_debits) || 0;
        });
      });

      return currencyGroups;
    },
    // Consolidated stats for display
    consolidatedStats() {
      const currencyGroups = this.statsByCurrency;

      return [
        {
          title: "Notas de crédito",
          values: Object.values(currencyGroups).map((group) => ({
            amount: group.total_credits,
            currencyCode: group.currency_code,
          })),
          color: "warning",
          isSingleValue: false,
        },
        {
          title: "Notas de débito",
          values: Object.values(currencyGroups).map((group) => ({
            amount: group.total_debits,
            currencyCode: group.currency_code,
          })),
          color: "info",
          isSingleValue: false,
        },
        {
          title: "Pagado",
          values: Object.values(currencyGroups).map((group) => ({
            amount: group.total_paid,
            currencyCode: group.currency_code,
          })),
          color: "success",
          isSingleValue: false,
        },
        {
          title: "Por pagar",
          values: Object.values(currencyGroups).map((group) => ({
            amount: group.pending_amount,
            currencyCode: group.currency_code,
          })),
          color: "error",
          isSingleValue: false,
        },
      ];
    },
    active_filters_count() {
      const simple = [this.filter_supplier, this.filter_status_id, this.filter_invoice_number]
        .filter(v => v !== null && v !== '' && v !== undefined).length;
      return simple + (this.filter_bank_account_ids.length > 0 ? 1 : 0);
    },
    // Cuentas bancarias de la unidad de negocio activa. business_unit_accounts trae una
    // fila por moneda, asi que se deduplica por account_id.
    filter_bank_accounts_list() {
      const business_unit = this.all_business_units.find(
        (element) => element.id == this.business_selected
      );
      if (!business_unit || !Array.isArray(business_unit.business_unit_accounts)) {
        return [];
      }
      const seen = new Set();
      const accounts = [];
      for (const item of business_unit.business_unit_accounts) {
        if (seen.has(item.account_id)) continue;
        seen.add(item.account_id);
        accounts.push({
          account_id: item.account_id,
          text: `${item.bank_description_long} ( Cta. ${item.account_check_digit} )`,
        });
      }
      return accounts.sort((a, b) => a.text.localeCompare(b.text));
    },
    supplier_filtered() {
      const list = [...this.all_suppliers];
      if (list.length > 0 && list[0].entity_id !== 0) {
        list.unshift({
          entity_id: 0,
          entity_name: "TODOS"
        });
      }
      return list;
    },
    invoices_filtered() {
      if (this.supplier_filter && this.supplier_filter !== 0) {
        return this.data_invoices.filter(
          (invoice) => invoice.entity_id == this.supplier_filter
        );
      }
      return this.data_invoices;
    },
    pendingAmountTotal() {
      let total = 0;

      this.selected.forEach(item => {
        total += parseFloat(item.pending_amount || 0);
      });

      return total;
    },
    available_to_pay() {
      let set_color = "error";
      if (this.amount_to_pay == 0) {
        return { amount: 0, color: set_color };
      }
      const available = parseFloat(this.amount_to_pay) - parseFloat(this.pendingAmountTotal);
      if (available >= 100) {
        set_color = "success";
      }
      if (available > 1 && available < 100) {
        set_color = "warning";
      }
      return { amount: available, color: set_color };
    },
    bank_account_with_currency() {
      const accountBank = [];
      let business_unit = this.all_business_units.filter((element) => element.id == this.business_selected);

      if (business_unit == undefined || business_unit.length == 0) {
        return accountBank;
      }
      for (let item of business_unit[0].business_unit_accounts) {
        accountBank.push({
          account_id: item.account_id,
          account_name: item.account_name,
          account_type_id: item.account_type_id,
          account_type_description: item.account_type_description,
          account_bank_id: item.account_bank_id,
          bank_description_long: item.bank_description_long,
          account_check_digit: item.account_check_digit,
          currency_id: item.currency_id,
          currency_description: item.currency_description,
          text: item.bank_description_long + " ( Cta. " + item.account_check_digit + " )",
          chart_of_account_id: item.chart_of_account_id,
        });
      }
      return accountBank.filter(
        (element) => element.currency_id == this.seleted_currencies
      );
    },
  },
  methods: {
    // ── Estado de cuenta de suplidor ──
    openStatement(entityId) {
      this.statementEntityId = Number(entityId);
      this.statementDialog = true;
    },
    openStatementFromSearch(item) {
      if (!item || !item.entity_id) return;
      this.openStatement(item.entity_id);
      this.$nextTick(() => { this.supplierSearch = null; });
    },
    async searchSuppliers(text) {
      this.supplierSearchLoading = true;
      try {
        const res = await this.api_Post("transactions/suppliers_search", {
          q: text, business_unit_id: this.business_selected, limit: 20,
        });
        if (res.status === 200) this.supplierSearchItems = await res.json();
      } catch (e) {
        console.error("Error buscando suplidores:", e);
      } finally {
        this.supplierSearchLoading = false;
      }
    },
    getCurrentWeekRange() {
      const today = moment();
      const startOfWeek = today.clone().startOf('week').format('YYYY-MM-DD'); // Sunday
      const endOfWeek = today.clone().endOf('week').format('YYYY-MM-DD'); // Saturday
      return { start: startOfWeek, end: endOfWeek };
    },
    async get_accounts() {
      this.all_business_units = await this.g_bussines();
    },
    isValidLocationValue(value) {
      if (value === null || value === undefined) return false;
      if (typeof value === "number" && value === 0) return true;
      if (typeof value === "object" && value.value !== undefined) return true;
      return typeof value === "number" && value > 0;
    },
    handlePay() {
      if (this.types_form === 0) {
        // Only for "Pagos múltiples" tab
        this.data_invoices_pay = [];
        if (this.selected.length > 0) {
          for (let item of this.selected) {
            this.data_invoices_pay.push({
              invoice_id: item.invoice_id,
              business_unit_id: item.business_unit_id,
              business_unit_description: item.business_unit_description,
              business_unit_rnc: item.business_unit_rnc,
              department_id: item.department_id,
              location_id: item.location_id,
              location_description: item.location_description,
              created_by: item.created_by,
              user_fullname: item.user_fullname,
              entity_id: item.entity_id,
              entity_name: item.entity_name,
              entity_contact_json: item.entity_contact_json,
              document_id_type_name: item.document_id_type_name,
              entity_document_id: item.entity_document_id,
              entity_email: item.entity_email,
              box_entry_id: item.box_entry_id,
              emission_date: item.emission_date,
              expire_at: item.expire_at,
              currency_id: item.currency_id,
              currency_name: item.currency_name,
              currency_code: item.currency_code,
              invoice_type_id: item.invoice_type_id,
              invoice_type_name: item.invoice_type_name,
              invoice_number: item.invoice_number,
              invoice_ncf: item.invoice_ncf,
              subtotal_amount: item.subtotal_amount,
              taxes_amount: item.taxes_amount,
              total_amount: item.total_amount,
              paid: parseFloat(item.pending_amount), //parseFloat(item.pending_amount) - parseFloat(item.total_paid === null ? 0 : item.total_paid),
              order_entry_id: item.order_entry_id,
              supplier_invoice_number: item.supplier_invoice_number,
              amount_formated: item.amount_formated,
              total_paid_amount: item.total_paid_amount,
              total_to_pay: item.total_to_pay,
              total_paid: item.total_paid,
              pending_amount: item.pending_amount,
              note: item.note,
              status_id: item.status_id,
              sta_description: item.sta_description,
              storage_id: item.storage_id,
              storage_json: item.storage_json,
              currency_rate: item.currency_rate,
              invoice_category_id: item.invoice_category_id,
            });
          }
          this.showCheckOutCxC = true;
        } else {
          this.$toast.warning("Debes seleccionar una factura");
        }
      }
    },
    updatePriceCurrency(invoice) {
      this.data_invoices_pay.forEach((element) => {
        if (element.invoice_id == invoice.invoice_id) {
          element.new_pending_amount = invoice.new_pending_amount;
          element.paid = parseFloat(invoice.new_pending_amount);
          element.rate = invoice.rate;
        }
      });
    },
    groupByEntityId(array) {
      if (array === null) return [];
      const grouped = {};
      array.forEach((item) => {
        const entityId = item.entity_id;
        if (!grouped[entityId]) {
          grouped[entityId] = [];
        }
        grouped[entityId].push(item);
      });
      return grouped;
    },
    colorRandom() {
      let r = Math.floor(Math.random() * 256);
      let g = Math.floor(Math.random() * 256);
      let b = Math.floor(Math.random() * 256);
      return (
        "#" +
        r.toString(16).padStart(2, "0") +
        g.toString(16).padStart(2, "0") +
        b.toString(16).padStart(2, "0")
      );
    },
    refreshData() {
      this.bank_to_preview = null;
      this.getData(this.lastData, true, true);
      this.showCheckOutCxC = false;
      this.selected = [];
    },
    removeInvoiceFromSelected(item) {
      const index = this.selected.findIndex(s => s.invoice_id === item.invoice_id);
      if (index !== -1) this.selected.splice(index, 1);
      const indexPay = this.data_invoices_pay.findIndex(s => s.invoice_id === item.invoice_id);
      if (indexPay !== -1) this.data_invoices_pay.splice(indexPay, 1);
    },
    // Arma los filtros SQL de facturas CxP en un solo lugar (antes estaba duplicado en
    // getData y loadPageData).
    //   base -> unidad, ubicaciones, fechas, moneda, antigüedad: define la lista de suplidores
    //   page -> suplidor y búsqueda por número: solo afecta a la página y a los totales
    buildInvoiceFilters(data) {
      let base = "";
      base += data.business == 0 ? "" : " AND business_unit_id=" + data.business;

      // Filtro de ubicación: si es null/0 (Todas), usar IN con ubicaciones de acceso
      if (data.location == null || data.location == 0) {
        base += ` AND LOCATION_ID IN (${this.$store.state.auth.locations.map(item => item.id).join(',')})`;
      } else {
        base += ` AND location_id=${data.location}`;
      }

      // Rango de fechas y moneda se omiten cuando se busca por número de factura
      if (!data.search_invoice) {
        if (this.useRangeFilter && data.dateStart && data.dateEnd) {
          base += ` AND EXPIRE_AT::DATE BETWEEN '${data.dateStart}' AND '${data.dateEnd}'`;
        } else if (data.dateEnd) {
          base += ` AND EXPIRE_AT::DATE <= '${data.dateEnd}'`;
        }

        base += this.seleted_currencies == null ? "" : " AND currency_id=" + this.seleted_currencies;
      }

      // Filtro de antigüedad
      if (data.aging_filter) {
        const today = moment().format('YYYY-MM-DD');
        switch (data.aging_filter) {
          case '0-30':
            base += ` AND EXPIRE_AT::DATE >= '${moment().subtract(30, 'days').format('YYYY-MM-DD')}'`;
            break;
          case '31-60':
            base += ` AND EXPIRE_AT::DATE BETWEEN '${moment().subtract(60, 'days').format('YYYY-MM-DD')}' AND '${moment().subtract(31, 'days').format('YYYY-MM-DD')}'`;
            break;
          case '61-90':
            base += ` AND EXPIRE_AT::DATE BETWEEN '${moment().subtract(90, 'days').format('YYYY-MM-DD')}' AND '${moment().subtract(61, 'days').format('YYYY-MM-DD')}'`;
            break;
          case '90+':
            base += ` AND EXPIRE_AT::DATE < '${moment().subtract(90, 'days').format('YYYY-MM-DD')}'`;
            break;
          case 'overdue':
            base += ` AND EXPIRE_AT::DATE < '${today}'`;
            break;
          case 'upcoming':
            base += ` AND EXPIRE_AT::DATE >= '${today}'`;
            break;
        }
      }

      let page = "";

      // Filtro de suplidor
      if (data.supplier_id && data.supplier_id !== 0) {
        page += ` AND entity_id = ${data.supplier_id}`;
      }

      // Filtro de búsqueda por número de factura (Server side)
      if (data.search_invoice) {
        page += ` AND (i.invoice_number ILIKE '%${data.search_invoice}%' OR i.supplier_invoice_number ILIKE '%${data.search_invoice}%')`;
      }

      return { base, page };
    },
    // refresh=true obliga al backend a recalcular los saldos (ignora su caché de 60 s):
    // se usa tras registrar/anular pagos y en el botón de refrescar.
    async getData(data, resetPage = true, refresh = false) {
      try {
        this.loaded = true;

        // Limpiar datos actuales para mostrar loading
        if (!resetPage) {
          this.data_invoices = [];
        }

        this.lastData = data;

        if (resetPage) {
          this.currentPage = 1;
        }

        const { base, page } = this.buildInvoiceFilters(data);

        // Una sola llamada: página + total + totales + suplidores en una misma pasada
        const response = await this.api_Post("transactions/invoice_shop_page", {
          filters: base,
          page_filters: page,
          page: this.currentPage,
          limit: this.itemsPerPage,
          refresh,
        });

        if (response.status == 200) {
          const result = await response.json();
          this.data_invoices = result.data || [];
          this.totalItems = result.total || 0;
          this.pageCount = Math.ceil(this.totalItems / this.itemsPerPage);
          this.globalTotals = result.totals || this.globalTotals;
          this.all_suppliers = result.suppliers || [];
        }

        this.last_filter = { filters: base, page_filters: page };
      } catch (error) {
        console.error("Error al cargar facturas:", error);
      } finally {
        this.loaded = false;
      }
    },
    async onPageChange() {
      if (this.lastData && Object.keys(this.lastData).length > 0) {
        // Solo cargar datos de la página, sin recargar totales
        await this.loadPageData(this.lastData);
      }
    },
    onItemsPerPageChange() {
      this.currentPage = 1;
      this.pageCount = Math.ceil(this.totalItems / this.itemsPerPage);
      this.onPageChange();
    },
    onTransactionsLimitChange() {
      this.transactionsPage = 1;
      this.getPayments(this.lastData, 1);
    },
    searchPayments() {
      this.transactionsPage = 1;
      this.getPayments(this.lastData, 1);
    },
    removeBankAccountFilter(accountId) {
      this.filter_bank_account_ids = this.filter_bank_account_ids.filter(id => id !== accountId);
    },
    applyFilters() {
      this.dialog_filters = false;
      this.transactionsPage = 1;
      this.getPayments(this.lastData, 1);
    },
    async openFiltersMenu() {
      this.dialog_filters = true;
      if (this.payment_states.length === 0) {
        const res = await this.api_Get("transactions/get_payment_states");
        if (res.status === 200) this.payment_states = await res.json();
      }
      await this.loadFilterSuppliers();
    },
    async loadFilterSuppliers() {
      this.filter_suppliers_loading = true;
      const data = this.lastData;
      let locationId = null;
      let locationIds = null;
      if (data?.location == null || data?.location == 0) {
        locationIds = this.$store.state.auth.locations.map(item => item.id);
      } else {
        locationId = data.location;
      }
      let dateStart = null;
      let dateEnd = null;
      if (this.types_form === 2) {
        dateStart = this.dateStart;
        dateEnd = this.date2;
      } else if (this.useRangeFilter && data?.dateStart && data?.dateEnd) {
        dateStart = data.dateStart;
        dateEnd = data.dateEnd;
      } else if (data?.dateEnd) {
        dateEnd = data.dateEnd;
      }
      const res = await this.api_Post("transactions/get_payment_suppliers_cxp", {
        business_unit_id: data?.business || null,
        location_id: locationId,
        location_ids: locationIds,
        date_start: dateStart,
        date_end: dateEnd,
      });
      if (res.status === 200) this.filter_suppliers_list = await res.json();
      this.filter_suppliers_loading = false;
    },
    async loadPageData(data) {
      // Cambio de página: misma llamada unificada (totales y suplidores no cambian)
      try {
        this.loaded = true;
        this.data_invoices = [];

        const { base, page } = this.buildInvoiceFilters(data);

        const response = await this.api_Post("transactions/invoice_shop_page", {
          filters: base,
          page_filters: page,
          page: this.currentPage,
          limit: this.itemsPerPage,
        });

        if (response.status == 200) {
          const result = await response.json();
          this.data_invoices = result.data || [];
          this.totalItems = result.total || 0;
          this.pageCount = Math.ceil(this.totalItems / this.itemsPerPage);
        }
      } catch (error) {
        console.error("Error al cargar página:", error);
      } finally {
        this.loaded = false;
      }
    },
    async getDataSupplier(data) {
      this.loaded = true;
      let filter = "and invoice_type_id IN (1,4) ";
      filter += data.business == 0 ? "" : " AND business_unit_id=" + data.business;

      // Filtro de ubicación: si es null/0 (Todas), usar IN con ubicaciones de acceso
      if (data.location == null || data.location == 0) {
        filter += ` AND LOCATION_ID IN (${this.$store.state.auth.locations.map(item => item.id).join(',')})`;
      } else {
        filter += ` AND location_id=${data.location}`;
      }

      filter += ` AND EXPIRE_AT::DATE <= '${data.dateEnd}'`;
      let resultClients = await this.api_Post("transactions/suppliers_pending_balances", {
        filters: filter,
      });
      if (resultClients.status == 200) {
        this.clients = await resultClients.json();
      }
      this.loaded = false;
    },
    getDate(item) {
      return moment(item).format("DD/MM/YYYY");
    },
    async getPayments(datas, page = 1) {
      this.loading = true;
      let filter = " where invoice_type_id  IN (1,4) ";
      filter += `AND payment_details::json->0->>'invoice_category_id' = '2'`;
      let data = datas || this.lastData;

      let dateStart = null;
      let dateEnd = null;
      let locationId = null;
      let locationIds = null;

      if (data != null) {
        filter += data.business === 0 ? "" : "AND business_unit_id=" + data.business;

        if (data.location == null || data.location == 0) {
          locationIds = this.$store.state.auth.locations.map(item => item.id);
          filter += ` AND LOCATION_ID IN (${locationIds.join(',')})`;
        } else {
          locationId = data.location;
          filter += ` AND location_id=${data.location}`;
        }

        if (this.types_form === 2) {
          if (!this.dateStart || !this.date2) {
            const weekRange = this.getCurrentWeekRange();
            this.dateStart = weekRange.start;
            this.date2 = weekRange.end;
          }
          dateStart = this.dateStart;
          dateEnd = this.date2;
          filter += ` AND payment_date::DATE BETWEEN '${dateStart}' AND '${dateEnd}'`;
        } else if (this.useRangeFilter && data.dateStart && data.dateEnd) {
          dateStart = data.dateStart;
          dateEnd = data.dateEnd;
          filter += ` AND payment_date::DATE BETWEEN '${dateStart}' AND '${dateEnd}'`;
        } else if (data.dateEnd) {
          dateEnd = data.dateEnd;
          filter += ` AND payment_date::DATE <= '${dateEnd}'`;
        }
      }

      let sanitizedSearch = null;
      if (this.search_payment && this.search_payment.trim() !== '') {
        let raw = this.search_payment.trim().replace(/['";\\\-\-]/g, '');
        const asInt = parseInt(raw, 10);
        sanitizedSearch = !isNaN(asInt) && /^\d+$/.test(raw) ? String(asInt) : raw;
        const searchTerm = sanitizedSearch.replace(/'/g, "''");
        filter += ` AND (payment_by_name ILIKE '%${searchTerm}%' OR payment_details::text ILIKE '%${searchTerm}%' OR CAST(invoice_payment_id AS TEXT) ILIKE '%${searchTerm}%')`;
      }

      // El boton de Excel exporta con esta cadena, no con los parametros de abajo,
      // asi que el filtro de cuentas tiene que quedar reflejado en ambos lados.
      const bankAccountIds = this.filter_bank_account_ids
        .map(id => parseInt(id, 10))
        .filter(id => !isNaN(id));
      if (bankAccountIds.length > 0) {
        filter += ` AND bank_account_id IN (${bankAccountIds.join(',')})`;
      }

      this.lastPaymentsFilter = filter;
      let resultPayments = await this.api_Post("transactions/view_payments_cxp", {
        business_unit_id: data?.business || null,
        location_id: locationId,
        location_ids: locationIds,
        date_start: dateStart,
        date_end: dateEnd,
        search: sanitizedSearch,
        filter_supplier: this.filter_supplier ?? null,
        filter_status_id: this.filter_status_id ?? null,
        filter_invoice_number: this.filter_invoice_number || null,
        filter_bank_account_ids: this.filter_bank_account_ids.length > 0 ? this.filter_bank_account_ids : null,
        page: page,
        limit: this.transactionsLimit
      });
      if (resultPayments.status == 200) {
        const response = await resultPayments.json();
        this.transactions = response.data;
        this.transactionsTotal = response.total;
        this.transactionsTotalPages = response.totalPages;
        this.transactionsPage = response.page;
      }
      this.loading = false;
    },
    onTransactionsPageChange(page) {
      this.getPayments(this.lastData, page);
    },
    async export_payment_details() {
      if (!this.lastPaymentsFilter) {
        this.$toast.warning('Aplica los filtros antes de exportar');
        return;
      }
      this.loading_export = true;
      const resultExport = await this.api_Post('transactions/viewAllPayemnts', {
        filters: this.lastPaymentsFilter,
      });
      let allData = await resultExport.json();
      this.loading_export = false;

      // Mismos filtros adicionales que aplica el listado (view_payments_cxp): suplidor y número de
      // factura buscan dentro del detalle del pago; el estado compara el id. Antes el Excel los ignoraba.
      const contains = (row, text) => JSON.stringify(row.payment_details || '').toLowerCase().includes(text.toLowerCase());
      if (this.filter_supplier && String(this.filter_supplier).trim() !== '') {
        allData = allData.filter(r => contains(r, String(this.filter_supplier).trim()));
      }
      if (this.filter_status_id != null) {
        allData = allData.filter(r => Number(r.status_id) === Number(this.filter_status_id));
      }
      if (this.filter_invoice_number && this.filter_invoice_number.trim() !== '') {
        allData = allData.filter(r => contains(r, this.filter_invoice_number.trim()));
      }

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Detalle de Pagos');

      worksheet.columns = [
        { header: 'ID Pago', key: 'invoice_payment_id', width: 12 },
        { header: 'Asiento', key: 'diary_book_code', width: 12 },
        { header: 'Fecha Efectiva Pago', key: 'payment_date', width: 15 },
        { header: 'Fecha Contable pago', key: 'account_date', width: 15 },
        { header: 'Pagado Por', key: 'payment_by_name', width: 30 },
        { header: 'Tipo de Pago', key: 'payment_type_name', width: 18 },
        { header: 'Cuenta Bancaria', key: 'account_name', width: 25 },
        { header: 'Banco', key: 'bank_account_description_long', width: 20 },
        { header: 'No. Cheque / Referencia', key: 'check_number', width: 20 },
        { header: 'Moneda transaccion', key: 'transaction_currency_code', width: 10 },
        { header: 'Monto transaccion', key: 'transaction_payment_amount', width: 16 },
        { header: 'Moneda base', key: 'base_currency_code', width: 10 },
        { header: 'Tasa moneda base', key: 'base_currency_rate', width: 16 },
        { header: 'Monto moneda base', key: 'base_amount', width: 16 },
        { header: 'Estado Pago', key: 'payment_status', width: 12 },
        { header: 'Contabilización', key: 'recorded_status', width: 18 },
        { header: 'Suplidor', key: 'entity_name', width: 25 },
        { header: 'RNC / Documento', key: 'entity_document_id', width: 18 },
        { header: 'Unidad de Negocio', key: 'business_unit_description', width: 22 },
        { header: 'Localidad', key: 'location_description', width: 22 },
        { header: 'Tipo Factura', key: 'invoice_type_name', width: 18 },
        { header: 'No. Factura', key: 'invoice_number', width: 20 },
        { header: 'NCF', key: 'invoice_ncf', width: 18 },
        { header: 'Fecha Emisión', key: 'emission_date', width: 15 },
        { header: 'Fecha Vencimiento', key: 'expire_at', width: 18 },
        { header: 'Moneda factura', key: 'invoice_currency_code', width: 14 },
        { header: 'Subtotal', key: 'subtotal_amount', width: 14 },
        { header: 'Impuestos', key: 'taxes_amount', width: 14 },
        { header: 'Total Factura', key: 'total_amount', width: 16 },
        { header: 'Nota', key: 'note', width: 35 },
      ];

      allData.forEach(group => {
        const recordedStatus = group.recorded ? 'Contabilizado' : 'Descontabilizado';
        const details = Array.isArray(group.payment_details) ? group.payment_details : [];
        details.forEach(detail => {
          if (group.status_id != 4 && [1, 4].includes(detail.invoice_type_id)) {
            worksheet.addRow({
              invoice_payment_id: group.invoice_payment_id,
              diary_book_code: group.diary_book_code,
              payment_date: group.payment_date,
              account_date: group.account_date,
              payment_by_name: group.payment_by_name,
              payment_type_name: group.payment_type_name,
              account_name: group.account_name,
              bank_account_description_long: group.bank_account_description_long,
              // Algunos registros traen el texto 'null' en vez de NULL; no debe salir en el reporte.
              check_number: group.check_number && group.check_number !== 'null' ? group.check_number : '',
              transaction_currency_code: group.currency_code,
              transaction_payment_amount: detail.transaction_amount,
              base_currency_code: detail.business_currency_code,
              base_currency_rate: 1 / parseFloat(detail.business_unit_currency_rate),
              base_amount: parseFloat(detail.business_unit_currency_rate) * parseFloat(detail.transaction_amount),
              payment_status: group.status_name,
              recorded_status: recordedStatus,
              entity_name: detail.entity_name,
              entity_document_id: detail.entity_document_id,
              business_unit_description: detail.business_unit_description,
              location_description: detail.location_description,
              invoice_type_name: detail.invoice_type_name,
              invoice_number: detail.invoice_number,
              invoice_ncf: detail.invoice_ncf,
              emission_date: detail.emission_date,
              expire_at: detail.expire_at,
              invoice_currency_code: detail.currency_code,
              subtotal_amount: detail.subtotal_amount,
              taxes_amount: detail.taxes_amount,
              total_amount: detail.total_amount,
              note: detail.note,
            });
          }
        });
      });

      worksheet.getRow(1).font = { bold: true };
      worksheet.autoFilter = {
        from: { row: 1, column: 1 },
        to: { row: 1, column: worksheet.columns.length },
      };

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/octet-stream' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `Pagos_CxP_${moment().format('YYYY-MM-DD')}.xlsx`;
      link.click();
    },
    async cancelPayment() {
      let result = await this.api_Post("transactions/cancel_payment_full", {
        payment_id: this.selectPayments.invoice_payment_id,
      });
      if (result.status == 200) {
        this.$toast.success("Pago anulado y descontabilizado exitosamente");
        this.dialogCancelPayment = false;
        this.getPayments();
      }
    },
    async reset() {
      this.dialog = false;
      this.loaded = true;
      await this.changeBusiness(this.lastData, true);
      this.loaded = false;
    },
    getDateColor(item) {
      const today = moment();
      const expireDate = moment(item.expire_at);
      if (expireDate.isBefore(today)) return "error";
      if (expireDate.diff(today, "days") <= 7) return "warning";
      return "success";
    },
    getDaysOverdue(item) {
      const today = moment();
      const expireDate = moment(item.expire_at);
      return today.diff(expireDate, "days");
    },
    getDaysOverdueColor(item) {
      const days = this.getDaysOverdue(item);
      if (days > 90) return "error darken-2";
      if (days > 60) return "error";
      if (days > 30) return "warning darken-1";
      if (days > 0) return "warning";
      if (days > -7) return "info";
      return "success";
    },
    getDaysOverdueTooltip(item) {
      const days = this.getDaysOverdue(item);
      if (days > 0) return `Vencida hace ${days} día${days > 1 ? 's' : ''}`;
      if (days === 0) return 'Vence hoy';
      return `Vence en ${Math.abs(days)} día${Math.abs(days) > 1 ? 's' : ''}`;
    },
    toggleRangeFilter() {
      this.useRangeFilter = !this.useRangeFilter;
      if (!this.useRangeFilter) {
        // Al desactivar rango, resetear fecha inicio
        this.dateStart = moment().subtract(90, 'days').format("YYYY-MM-DD");
      } else if (this.types_form === 2) {
        // When activating range filter on payments tab, use current week
        const weekRange = this.getCurrentWeekRange();
        this.dateStart = weekRange.start;
        this.date2 = weekRange.end;
      }
      this.update_filters();
    },
    async onDashboardCurrencyChange(currencyId) {
      this.dashboardCurrencyChanging = true;
      this.seleted_currencies = currencyId;
      await this.calculateDashboardData();
    },
    async onDashboardConvertChange({ convert_to, currency_to }) {
      this.dashboardConvertTo = convert_to;
      this.dashboardCurrencyTo = currency_to;
      await this.calculateDashboardData();
    },
    async calculateDashboardData() {
      if (!this.lastData) return;
      if (this._dashboardCalculating) return;
      this._dashboardCalculating = true;

      this.dashboardLoading = true;
      try {
        let filter = "";
        filter += this.lastData.business == 0 ? "" : " AND business_unit_id=" + this.lastData.business;

        // Filtro de ubicación: si es null/0 (Todas), usar IN con ubicaciones de acceso
        if (this.lastData.location == null || this.lastData.location == 0) {
          filter += ` AND LOCATION_ID IN (${this.$store.state.auth.locations.map(item => item.id).join(',')})`;
        } else {
          filter += ` AND location_id=${this.lastData.location}`;
        }

        if (this.useRangeFilter && this.lastData.dateStart && this.lastData.dateEnd) {
          filter += ` AND EXPIRE_AT::DATE BETWEEN '${this.lastData.dateStart}' AND '${this.lastData.dateEnd}'`;
        } else if (this.lastData.dateEnd) {
          filter += ` AND EXPIRE_AT::DATE <= '${this.lastData.dateEnd}'`;
        }

        if (!this.dashboardConvertTo) {
          filter += this.seleted_currencies == null ? "" : " AND currency_id=" + this.seleted_currencies;
        }

        this.dashboardFilters = filter;

        const response = await this.api_Post("transactions/invoice_shop_dashboard", {
          filters: filter,
          convert_to: this.dashboardConvertTo,
          currency_to: this.dashboardConvertTo ? this.dashboardCurrencyTo : null,
          business: this.lastData.business
        });

        if (response.status === 200) {
          const data = await response.json();

          // Update KPIs
          this.dashboardData.kpis = {
            totalPending: parseFloat(data.total_pending || 0),
            overdueCount: parseInt(data.overdue_count || 0),
            avgDPO: Math.round(parseFloat(data.avg_dpo || 0)),
            activeSuppliers: parseInt(data.active_suppliers || 0),
            overdueAmount: parseFloat(data.overdue_amount || 0)
          };

          // Update Aging
          this.dashboardData.aging = {
            range0_30: parseFloat(data.aging_0_30 || 0),
            range31_60: parseFloat(data.aging_31_60 || 0),
            range61_90: parseFloat(data.aging_61_90 || 0),
            range91_120: parseFloat(data.aging_91_120 || 0),
            range120plus: parseFloat(data.aging_120plus || 0)
          };

          // Update Status
          this.dashboardData.status = {
            overdue: parseInt(data.status_overdue || 0),
            upcoming: parseInt(data.status_upcoming || 0),
            onTime: parseInt(data.status_on_time || 0)
          };

          // Update Top Suppliers
          this.dashboardData.topSuppliers = data.top_suppliers || [];

          // Update Stacked Aging + Pareto + Locations
          this.dashboardData.agingStacked = data.aging_stacked || [];
          this.dashboardData.paretoData = data.pareto_data || [];
          this.dashboardData.locations = data.locations || [];
          this.dashboardData.agingTable = data.aging_table || [];

          // Update Currencies
          this.dashboardData.currencies = data.currencies || [];

          // Update DPO
          this.dashboardData.dpo.value = this.dashboardData.kpis.avgDPO;

          // Force reactivity - reassign entire object so child watcher triggers with all data
          this.dashboardData = { ...this.dashboardData };
        }
      } catch (error) {
        console.error("Error al calcular datos del dashboard:", error);
      } finally {
        this.dashboardLoading = false;
        this._dashboardCalculating = false;
      }
    },
    update_filters() {
      this.changeBusiness({
        business: this.business_selected,
        businessData: this.business_selected,
        location: this.locationId,
        dateStart: this.dateStart,
        dateEnd: this.date2,
        aging_filter: this.aging_filter,
        supplier_id: this.supplier_filter,
        search_invoice: this.search_invoice.replace(/['"]/g, '').trim()
      });
    },
    async changeBusiness(data, refresh = false) {
      this.hasData = true;
      if (this.types_form === 0) {
        await this.getData(data, true, refresh);
      } else if (this.types_form === 1) {
        await this.getDataSupplier(data);
      } else if (this.types_form === 2) {
        await this.getPayments(data);
      }
      const resultCurrencies = await this.api_Post("accounting/currenciesBusiness", {
        business: data.business,
        effective_date: new Date(),
      });
      this.currencies = await resultCurrencies.json();
      this.allSystemCurrencies = this.currencies;
      this.lastData = data;
    },
    async viewInvoices(item) {
      this.loaded = true;
      let data = {
        entity_id: item.entity_id,
        busines_unit: this.business_selected,
        location_access: this.$store.state.auth.locations.map(item => item.id),
        date_end: this.lastData.dateEnd,
        location_id: this.lastData.location,
      };
 
      let responseRequest = await this.api_Post("transactions/invoice_supplier", data);
      if (responseRequest.status == 200) {
        this.selectedClients = await responseRequest.json();
        this.dialog = true;
      }
      this.loaded = false;
    },
    getStats(step) {
      if (step === 1) {
        return [
          {
            title: "Cantidad Facturas",
            value: this.globalTotals.total_invoices || 0,
            color: "primary",
            format: "integer",
          },
          {
            title: "Por pagar",
            value: parseFloat(this.globalTotals.total_pending || 0),
            color: "error",
            format: "currency",
          },
          {
            title: "Notas de crédito",
            value: parseFloat(this.globalTotals.total_credits || 0),
            color: "success",
            format: "currency",
          },
          {
            title: "Pagado",
            value: parseFloat(this.globalTotals.total_paid || 0),
            color: "success",
            format: "currency",
          },
        ];
      } else if (step === 2) {
        return [
          {
            title: "Cantidad Facturas",
            value: this.clients.reduce(
              (sum, value) => sum + parseFloat(value.invoice_quantity),
              0
            ),
            color: "primary",
          },
          {
            title: "Por pagar",
            value: this.clients.reduce(
              (sum, value) => sum + parseFloat(value.total_pending_amount),
              0
            ),
            color: "error",
          },
          {
            title: "Notas de crédito",
            value: this.clients.reduce(
              (sum, value) => sum + parseFloat(value.total_credit_amount),
              0
            ),
            color: "warning",
          },
          {
            title: "Pagado",
            value: this.clients.reduce(
              (sum, value) => sum + parseFloat(value.total_paid_amount),
              0
            ),
            color: "success",
          },
        ];
      }
      return [];
    },
    getStatIcon(title) {
      const icons = {
        'Cantidad Facturas': 'mdi-file-document-multiple-outline',
        'Por pagar': 'mdi-alert-circle-outline',
        'Notas de crédito': 'mdi-credit-card-refund-outline',
        'Notas de débito': 'mdi-credit-card-plus-outline',
        'Pagado': 'mdi-check-circle-outline',
      };
      return icons[title] || 'mdi-information-outline';
    },
    getIconForStep(step) {
      const icons = [
        "mdi-file-document-multiple",
        "mdi-account-group",
        "mdi-cash-register",
        "mdi-chart-bar",
      ];
      return icons[step - 1];
    },
    getTitleForStep(step) {
      const titles = ["Facturas", "Suplidores", "Pagos", "Reportes"];
      return titles[step - 1];
    },
    getHeadersForStep(step) {
      if (step === 1) return this.headersInvoices;
      if (step === 2) return this.headers;
      if (step === 3) return this.headersTransactions;
      return [];
    },
    // Hay una v-data-table por tab (v-for="page in 4"); `tab` llega desde el template.
    // Solo el tab 2 (suplidores) pagina en cliente.
    onTablePageUpdate(tab, value) {
      if (tab === 2) this.supplierPage = value;
    },
    onTablePagination(tab, pagination) {
      if (tab === 2) this.supplierPagination = pagination;
    },
    getItemsForStep(step) {
      if (step === 1) return this.invoices_filtered;
      if (step === 2) return this.clients;
      if (step === 3) return this.transactions;
      return [];
    },
    // reload=true cuando el usuario cambia la unidad de negocio: antes solo se actualizaba el combo
    // de sucursales y la pantalla seguía mostrando los datos de la unidad anterior.
    updateLocationsForBusiness(reload = false) {
      // Las cuentas seleccionadas pertenecen a la unidad anterior; dejarlas activas
      // devolveria cero resultados sin explicacion.
      if (reload) {
        this.filter_bank_account_ids = [];
      }
      if (!this.$store.state.auth.locations || this.$store.state.auth.locations.length === 0) {
        setTimeout(() => {
          if (this.$store.state.auth.locations && this.$store.state.auth.locations.length > 0) {
            this.updateLocationsForBusiness();
          }
        }, 500);
        this.update_filters();
        return;
      }

      this.itemsLocations2 = [];

      if (this.business_selected && this.business_selected != 0) {
        this.itemsLocations2 = this.updateCombo(
          this.itemsLocations,
          this.itemsLocations2,
          this.business_selected,
          "location"
        );
      } else {
        this.itemsLocations2 = [...this.itemsLocations];
        this.itemsLocations2.unshift({ text: "Todas", value: 0 });
      }

      if (reload) {
        this.selected = [];
        this.clients = [];
        this.update_filters();
      }
    },
    updateCombo(itemsOrigen, itemsDestino, condicion, combo) {
      if (!itemsOrigen || !Array.isArray(itemsOrigen) || itemsOrigen.length === 0) {
        itemsDestino.splice(0, itemsDestino.length);
        itemsDestino.unshift({ text: "Todas", value: 0 });
        return itemsDestino;
      }

      itemsDestino.splice(0, itemsDestino.length);

      for (let item of itemsOrigen) {
        if (item.bus_id === condicion) {
          itemsDestino.push({
            value: item.value,
            text: item.text,
            routes: item.routes,
          });
        }
      }
      itemsDestino.unshift({ text: "Todas", value: 0 });

      // Validar selección actual
      const hasValidSelection =
        this.location_selected &&
        (typeof this.location_selected === "object" ||
          (typeof this.location_selected === "number" && this.location_selected !== 0));

      if (!hasValidSelection) {
        this.location_selected = itemsDestino[0];
      } else {
        const currentSelectionExists = itemsDestino.find((item) => {
          if (typeof this.location_selected === "object") {
            return item.value === this.location_selected.value;
          } else {
            return item.value === this.location_selected;
          }
        });

        // La sucursal seleccionada pertenece a otra unidad: volver a "Todas" (antes quedaba
        // una sucursal ajena y los filtros devolvían 0 filas)
        if (!currentSelectionExists && this.location_selected.value !== 0) {
          this.location_selected = itemsDestino[0];
        } else if (currentSelectionExists && typeof this.location_selected === "number") {
          // Normalizar a objeto: el combo es return-object y el resto de la vista lee .value
          this.location_selected = currentSelectionExists;
        }
      }

      return itemsDestino;
    },
    async fetchAllPendingInvoices() {
      // Obtiene TODAS las facturas pendientes que coinciden con el filtro actual,
      // ignorando la paginación de la tabla (para el reporte "Todo").
      const response = await this.api_Post("transactions/invoice_shop_page", {
        ...(this.last_filter || { filters: "", page_filters: "" }),
        page: 1,
        limit: this.totalItems > 0 ? this.totalItems : 100000,
      });

      if (response.status == 200) {
        const result = await response.json();
        return result.data || [];
      }

      throw new Error("No se pudieron obtener todas las facturas pendientes");
    },
    async generate_preview() {
      // Bloqueo de doble clic: el previo "Todo" descarga todas las facturas pendientes
      if (this.generatingPreview) return;
      this.generatingPreview = true;
      try {
        await this.generate_preview_run();
      } catch (e) {
        console.error("Error generando el previo:", e);
        this.$toast.error("No se pudo generar el previo");
      } finally {
        this.generatingPreview = false;
      }
    },
    async generate_preview_run() {
      if (this.bank_to_preview == null) {
        this.$toast.error("Debe seleccionar una cuenta de banco");
        return;
      }

      // Para "Todo" se traen todas las facturas pendientes (todas las páginas);
      // para "Seleccion" se usan únicamente las filas seleccionadas.
      let invoices;
      if (this.preview_type == '1') {
        try {
          this.loaded = true;
          invoices = await this.fetchAllPendingInvoices();
        } catch (error) {
          console.error("Error al obtener todas las facturas para el previo:", error);
          this.$toast.error("Error al obtener todas las facturas pendientes");
          this.loaded = false;
          return;
        } finally {
          this.loaded = false;
        }
      } else {
        invoices = this.selected;
      }

      if (!invoices || invoices.length === 0) {
        this.$toast.error("No hay facturas para imprimir");
        return;
      }

      const doc = new jsPDF({
        orientation: "portrait",
        unit: "pt",
        format: "letter"
      });

      const left = 40; // margen izquierdo
      let y = 40;      // posición vertical

      // ===============================
      //  ENCABEZADO PRINCIPAL DEL REPORTE
      // ===============================
      // TÍTULO CENTRADO Y EN NEGRITA
      doc.setFontSize(12);
      doc.setFont(undefined, "bold");
      doc.text("Grupo M", doc.internal.pageSize.getWidth() / 2, y, { align: "center" });
      y += 18;

      // SUBTÍTULO CENTRADO Y EN NEGRITA
      doc.setFontSize(14);
      doc.setFont(undefined, "bold");
      doc.text(
        "Reporte Previo a la Impresión de Cheques",
        doc.internal.pageSize.getWidth() / 2,
        y,
        { align: "center" }
      );
      y += 25;

      // Regresar a fuente normal
      doc.setFont(undefined, "normal");


      // Datos superiores
      doc.setFontSize(10);
      // doc.text(`Ciclo de Pago : LM03T`, left, y);
      // y += 15;
      const businessUnit = this.itemsBussines.find((bu) => bu.value === this.business_selected);
      doc.text(`Unidad de Negocio : ${businessUnit.text}`, left, y);
      doc.text(`Banco: ${this.bank_to_preview.account_name}`, left, y + 15);

      // Parte derecha (Fecha / Hora / Página)
      doc.text(`Nº Página: 1`, 480, 40);
      doc.text(`Fecha Ejec: ${new Date().toLocaleDateString()}`, 480, 55);
      doc.text(`Hora Ejec: ${new Date().toLocaleTimeString()}`, 480, 70);

      y += 25;
      doc.line(left, y, 560, y);
      y += 20;

      // ===============================
      //  AGRUPAR POR SUPLIDOR (entity_id)
      // ===============================
      const proveedores = {};
      invoices.forEach(item => {
        if (!proveedores[item.entity_id]) {
          proveedores[item.entity_id] = [];
        }
        proveedores[item.entity_id].push(item);
      });

      // ===============================
      //  RECORRER LOS SUPLIDORES
      // ===============================
      for (const supplierId in proveedores) {
        const rows = proveedores[supplierId];
        const supplierName = rows[0].entity_name;

        // SUPLIDOR HEADER
        doc.setFontSize(11);
        doc.text(`No. Suplidor : ${supplierId}   ${supplierName}`, left, y);
        y += 18;

        // ===============================
        // TABLA POR SUPLIDOR
        // ===============================
        autoTable(doc, {
          startY: y,
          margin: { left },
          head: [[
            "Comprobante",
            "Factura",
            "F. Factura",
            "F. Vcto",
            "Moneda",
            "Monto Factura",
            "Descuento",
            "Neto Pagar"
          ]],
          body: rows.map(r => [
            r.invoice_number,                        // comprobante
            r.supplier_invoice_number || "",         // factura
            this.formatDate(r.emission_date),        // fecha factura
            this.formatDate(r.expire_at),            // fecha vencimiento
            r.currency_code,
            this.formatNumber(r.total_amount),
            "0.00",
            this.formatNumber(r.pending_amount)
          ]),
          theme: "plain",
          styles: { fontSize: 9, cellPadding: 3 },
          headStyles: { fontStyle: "bold" },
          columnStyles: {
            2: { halign: "right" }, // F. Factura
            3: { halign: "right" }, // F. Vcto
            5: { halign: "right" }, // Monto Factura
            6: { halign: "right" }, // Descuento
            7: { halign: "right" }, // Neto Pagar
          }
        });
        const pageCount = doc.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
          doc.setPage(i);
          doc.setFontSize(9);
          if (i == 1) {
            continue
          }
          doc.text(
            `Página ${i} de ${pageCount}`,
            doc.internal.pageSize.getWidth() - 20,
            doc.internal.pageSize.getHeight() - 10,
            { align: "right" }
          );
        }
        y = doc.autoTable.previous.finalY + 10;

        // TOTAL POR PROVEEDOR
        const totalProveedor = rows.reduce((sum, r) => sum + parseFloat(r.pending_amount), 0);

        doc.setFontSize(10);
        doc.text(
          `Total por Proveedor :   ${this.formatNumber(totalProveedor)}`,
          left + 350,
          y
        );

        y += 25;

        // Salto de página si es necesario
        if (y > 700) {
          doc.addPage();
          y = 40;
        }
      }
      const limpiarString = (str) => {
        return str
          .normalize("NFD").replace(/[\u0300-\u036f]/g, "") // quita acentos
          .replace(/[^a-zA-Z0-9 ]/g, ""); // elimina otros caracteres especiales
      };

      // DESCARGAR PDF
      doc.save(`Reporte Previo ${limpiarString(businessUnit.text)} al ${this.lastData.dateEnd} .pdf`);
    },

    formatDate(date) {
      if (!date) return "";
      const d = new Date(date);
      return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
    },

    formatNumber(val) {
      return Number(val).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    }

  },
};
</script>

<style lang="scss" scoped>
// ── Palette aligned with the Vuetify theme (teal primary, MD tokens) ──
$ink:        #2A3547;
$muted:      #5A6A85;
$muted-soft: #8A98AB;
$rule:       #E5EAF2;
$bg:         #F7F9FC;
$card:       #FFFFFF;

$primary:      #00897B;
$primary-soft: #E0F2F1;
$info:         #2196F3;
$info-soft:    #E3F2FD;
$warning:      #F57F17;
$warning-soft: #FFF3E0;
$success:      #2E7D32;
$success-soft: #E8F5E9;
$error:        #E53935;
$error-soft:   #FFEBEE;

$shadow:    rgb(145 158 171 / 20%) 0px 0px 2px 0px, rgb(145 158 171 / 12%) 0px 8px 18px -6px;
$shadow-lg: rgb(145 158 171 / 20%) 0px 0px 2px 0px, rgb(145 158 171 / 16%) 0px 14px 28px -6px;
$font: 'Plus Jakarta Sans', sans-serif;

.fc-cxp {
  font-family: $font;
  color: $ink;
  background: $bg;
  min-height: 100vh;
  padding: clamp(18px, 2.4vw, 32px) clamp(16px, 3vw, 40px) 56px;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

// ── HERO ─────────────────────────────────────────────────────
.fc-hero {
  position: relative;
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 24px;
  align-items: center;
  padding: clamp(22px, 2.6vw, 34px);
  border-radius: 16px;
  background: linear-gradient(120deg, #ffffff 0%, #f3faf8 100%);
  border: 1px solid $rule;
  box-shadow: $shadow;
  overflow: hidden;
}
.fc-hero::before {
  content: "";
  position: absolute;
  inset: 0 0 0 auto;
  width: 5px;
  background: linear-gradient(180deg, $primary, lighten($primary, 18%));
}
.fc-hero__eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: .06em;
  text-transform: uppercase;
  color: $primary;
  margin-bottom: 10px;
}
.fc-hero__title {
  font-family: $font;
  font-size: clamp(26px, 3.2vw, 36px);
  font-weight: 700;
  line-height: 1.1;
  letter-spacing: -.01em;
  color: $ink;
  margin: 0 0 10px;
}
.fc-hero__lede {
  font-size: 14px;
  line-height: 1.55;
  color: $muted;
  max-width: 60ch;
  margin: 0;
}
.fc-hero__lede strong { color: $ink; font-weight: 700; }

.fc-hero__right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 12px;
}
.fc-hero__date { display: flex; align-items: center; gap: 12px; }
.fc-hero__date-day {
  font-size: 52px;
  font-weight: 700;
  line-height: 1;
  letter-spacing: -.03em;
  color: $primary;
}
.fc-hero__date-rest {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1.2;
}
.fc-hero__date-month { font-size: 13px; font-weight: 700; letter-spacing: .14em; color: $ink; }
.fc-hero__date-year  { font-size: 13px; font-weight: 600; color: $muted; }

.fc-chip-live {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .05em;
  text-transform: uppercase;
  color: $primary;
  padding: 6px 12px;
  border-radius: 999px;
  background: $primary-soft;
}
.fc-chip-live__dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: $primary;
  box-shadow: 0 0 0 3px rgba(0, 137, 123, .18);
  animation: fc-pulse 1.8s ease-in-out infinite;
}
@keyframes fc-pulse {
  0%, 100% { box-shadow: 0 0 0 3px rgba(0, 137, 123, .18); }
  50%      { box-shadow: 0 0 0 8px rgba(0, 137, 123, 0); }
}

// ── SECTION ──────────────────────────────────────────────────
.fc-section { margin: 0; }
.fc-section__head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
}
.fc-section__title {
  font-size: 16px;
  font-weight: 700;
  letter-spacing: -.01em;
  color: $ink;
  margin: 0;
}

// ── CARD ─────────────────────────────────────────────────────
.fc-card {
  background: $card;
  border: 1px solid $rule;
  border-radius: 14px;
  box-shadow: $shadow;
}
.fc-card--filters { padding: 14px clamp(12px, 1.4vw, 18px) 6px; }
.fc-card--board,
.fc-card--table { overflow: hidden; }

// ── KPI ──────────────────────────────────────────────────────
.fc-kpis {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 18px;
}
.fc-kpi {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 20px 20px 18px;
  background: $card;
  border: 1px solid $rule;
  border-radius: 14px;
  box-shadow: $shadow;
  overflow: hidden;
  transition: transform .2s ease, box-shadow .2s ease;
}
.fc-kpi:hover { transform: translateY(-3px); box-shadow: $shadow-lg; }
.fc-kpi__top { display: flex; align-items: center; justify-content: space-between; margin-bottom: 4px; }
.fc-kpi__icon {
  display: grid;
  place-items: center;
  width: 44px;
  height: 44px;
  border-radius: 12px;
}
.fc-kpi--primary .fc-kpi__icon { background: $primary-soft; }
.fc-kpi--warning .fc-kpi__icon { background: $warning-soft; }
.fc-kpi--info    .fc-kpi__icon { background: $info-soft; }
.fc-kpi--success .fc-kpi__icon { background: $success-soft; }
.fc-kpi--error   .fc-kpi__icon { background: $error-soft; }
.fc-kpi__label {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: .04em;
  text-transform: uppercase;
  color: $muted;
}
.fc-kpi__values { display: flex; flex-direction: column; gap: 2px; margin-top: 2px; }
.fc-kpi__money { display: flex; align-items: baseline; gap: 6px; }
.fc-kpi__number {
  font-size: 22px;
  font-weight: 800;
  line-height: 1.25;
  letter-spacing: -.02em;
  color: $ink;
}
.fc-kpi__currency { font-size: 12px; font-weight: 700; color: $muted-soft; text-transform: uppercase; }

// ── TABS / BOARD ─────────────────────────────────────────────
.fc-tabs { padding: 0 8px; }
.fc-tab {
  font-family: $font;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0;
  text-transform: none;
}
.fc-board__pane { padding: clamp(14px, 1.8vw, 24px); }
.fc-panes { background: transparent; overflow: visible; }

// ── TABLE HEAD ───────────────────────────────────────────────
.fc-table__head {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding: 12px clamp(14px, 1.6vw, 20px);
  border-bottom: 1px solid $rule;
}
.fc-table__title {
  display: inline-flex;
  align-items: center;
  font-size: 15px;
  font-weight: 700;
  color: $ink;
}

@media (max-width: 720px) {
  .fc-hero { grid-template-columns: 1fr; }
  .fc-hero__right { flex-direction: row; align-items: center; justify-content: space-between; }
  .fc-kpis { grid-template-columns: 1fr 1fr; }
}
@media (max-width: 460px) {
  .fc-kpis { grid-template-columns: 1fr; }
}

// ── Vuetify deep overrides ───────────────────────────────────
::v-deep .fc-card--filters {
  .v-input__slot { background: #F7F9FC !important; min-height: 42px !important; }
  .v-input fieldset { border-color: $rule !important; border-radius: 8px !important; }
  .v-input--is-focused fieldset { border-color: $primary !important; }
  .v-label { font-family: $font !important; color: $muted !important; }
  .v-select__selection,
  .v-text-field input { font-family: $font !important; font-size: 14px !important; color: $ink !important; }
}

::v-deep .fc-data-table {
  background: transparent;

  .v-data-table-header th {
    background: #F7F9FC;
    color: $muted !important;
    font-weight: 700 !important;
    font-size: 11px !important;
    letter-spacing: .04em;
    text-transform: uppercase;
    white-space: nowrap;
  }
  td { font-family: $font; color: $ink; }
  td, th { border-bottom: 1px solid $rule !important; }
  tbody tr { transition: background .18s ease; }
  tbody tr:hover { background: $primary-soft !important; }
  .v-data-footer { border-top: 1px solid $rule; font-family: $font; }
}

::v-deep .fc-tabs .v-tabs-items,
::v-deep .v-window__container { overflow: visible !important; }

/* ═══════ STAT CARDS ═══════ */
.stat-card {
  border-left: 4px solid transparent;
  border-radius: 8px !important;
  background: #fafbfc;
  transition: box-shadow 0.25s ease, transform 0.2s ease;
}

.stat-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08) !important;
  transform: translateY(-2px);
}

.stat-card--primary {
  border-left-color: var(--v-primary-base, #1976D2);
}

.stat-card--error {
  border-left-color: var(--v-error-base, #FF5252);
}

.stat-card--success {
  border-left-color: var(--v-success-base, #4CAF50);
}

.stat-card--warning {
  border-left-color: var(--v-warning-base, #FB8C00);
}

.stat-card--info {
  border-left-color: var(--v-info-base, #2196F3);
}

.stat-icon-avatar {
  flex-shrink: 0;
}

.stat-label {
  letter-spacing: 0.5px;
  line-height: 1.2;
}

.stat-value {
  line-height: 1.3;
  margin-top: 2px;
}

/* ═══════ SELECTION BAR ═══════ */
.selection-bar {
  background: #f5f7fa !important;
  border: 1px solid #e0e0e0;
  border-radius: 8px !important;
  transition: all 0.3s ease;
}

.selection-bar--active {
  background: linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%) !important;
  border-color: var(--v-primary-base, #1976D2);
  box-shadow: 0 2px 8px rgba(25, 118, 210, 0.12);
}

.selection-item {
  padding: 4px 8px;
}

.selection-label {
  font-size: 0.8125rem;
  color: #616161;
  font-weight: 500;
  white-space: nowrap;
}

.selection-value {
  font-size: 0.9375rem;
  white-space: nowrap;
}

.selection-input {
  font-size: 0.875rem;
}

.selection-input ::v-deep .v-input__slot {
  min-height: 28px !important;
  padding: 0 8px !important;
}

.selection-input ::v-deep input {
  padding: 2px 0 !important;
  font-weight: 600;
}

/* ═══════ TABLE FOOTER ═══════ */
.table-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  padding: 10px 16px;
  background: linear-gradient(180deg, #f8f9fb 0%, #eef0f4 100%);
  border-top: 1px solid #e0e0e0;
}

.table-footer__left,
.table-footer__center,
.table-footer__right {
  display: flex;
  align-items: center;
}

.table-footer__left {
  flex: 1;
  min-width: 200px;
}

.table-footer__center {
  flex: 0 0 auto;
}

.table-footer__right {
  flex: 1;
  min-width: 200px;
  justify-content: flex-end;
}

.table-footer__label {
  font-size: 0.75rem;
  color: #757575;
  font-weight: 500;
  white-space: nowrap;
}

.table-footer__toggle {
  border-radius: 6px !important;
  overflow: hidden;
}

.table-footer__toggle-btn {
  min-width: 36px !important;
  height: 26px !important;
  font-size: 0.7rem !important;
  font-weight: 600 !important;
  letter-spacing: 0.2px;
  padding: 0 6px !important;
}

.table-footer__info-chip {
  font-size: 0.75rem;
  height: 26px !important;
  letter-spacing: 0.3px;
}

/* Compact pagination */
.table-footer__right ::v-deep .v-pagination .v-pagination__item,
.table-footer__right ::v-deep .v-pagination .v-pagination__navigation {
  height: 28px;
  min-width: 28px;
  font-size: 0.75rem;
  margin: 0 2px;
  box-shadow: none;
}
</style>
